import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { Modal, Dialog, DialogTitle, DialogContent } from '@mui/material';
import ToolBreadcrumb from 'components/Common/ToolBreadcrumb';
import { useSnackbar } from 'notistack';
import { GlobalContext } from 'providers/GlobalProvider';
import NotificationAPI from 'api/NotificationsAPI';
import CasesAPI from 'api/CasesAPI';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { renderFieldWithLinks } from 'utils/linkifyText';
import CustomsAnalyzerAPI, {
  CustomsUploadProgress,
} from 'api/CustomsAnalyzerAPI';
import DocumentLibraryAPI from 'api/DocumentLibraryAPI';
import {
  bulkUploadImportProducts,
  createImportDatabase,
  createImportProduct,
  fetchImportDatabaseById,
  fetchImportDatabasesByGroupId,
  fetchImportProductsByDatabaseId,
  findCurrentHtsCodes,
  waitForImportProductJob,
  CurrentHtsOption,
  ImportServiceDatabase,
} from 'pages/import/lib/import-service';
// Reuses the shared "standard workflow" chat shell CSS (same classes as
// EARChatUI / the import & Beneficial Ownership assistants) so this page's
// two-column layout, headers, and card chrome are visually identical to
// those tools -- only the CSS is shared here, not their components, since
// this tool needs its own composer (file attach) and its own testable
// mode-picker markup.
import 'resources/styles/standard-workflow.css';
import { useScrollWorkflowIntoView } from 'components/Workflows/StandardWorkflowLayout';

const ANALYSIS_MODES = [
  {
    value: 'package-check',
    label: 'Package checker',
    description: 'Verify all expected documents are present.',
  },
  {
    value: 'ingest',
    label: 'Entry parsing',
    description: 'Extract structured fields and line items into CSV/JSON.',
  },
  {
    value: 'analyze',
    label: 'Full comparison',
    description: 'Parse the entry and run the full discrepancy check.',
  },
];

type StyleValue =
  | string
  | number
  | null
  | undefined
  | Record<string, string | number | null | undefined>;
// Declared as an interface (not `type SxStyle = Record<string, … | SxStyle>`)
// so the self-reference in the index signature is legal — the Record form
// trips TS2456 "Type alias circularly references itself".
interface SxStyle {
  [key: string]: StyleValue | SxStyle;
}

const spacingToPx = (value: number): string => `${value * 8}px`;
const radiusToPx = (value: number): string => `${value * 4}px`;

const currentBreakpoint = (): 'xs' | 'sm' | 'md' | 'lg' | 'xl' => {
  if (typeof window === 'undefined') {
    return 'lg';
  }

  const width = window.innerWidth;
  if (width >= 1536) {
    return 'xl';
  }
  if (width >= 1200) {
    return 'lg';
  }
  if (width >= 900) {
    return 'md';
  }
  if (width >= 600) {
    return 'sm';
  }
  return 'xs';
};

const resolveResponsiveValue = (
  value: StyleValue,
): string | number | null | undefined => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  const responsive = value as Record<
    string,
    string | number | null | undefined
  >;
  const order: Array<'xs' | 'sm' | 'md' | 'lg' | 'xl'> = [
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
  ];
  const activeIndex = order.indexOf(currentBreakpoint());

  for (let index = activeIndex; index >= 0; index -= 1) {
    const breakpoint = order[index];
    if (responsive[breakpoint] !== undefined) {
      return responsive[breakpoint];
    }
  }

  return (
    responsive.xs ??
    responsive.sm ??
    responsive.md ??
    responsive.lg ??
    responsive.xl
  );
};

const unitlessCssProperties = new Set([
  'flex',
  'flexGrow',
  'flexShrink',
  'fontWeight',
  'lineHeight',
  'opacity',
  'order',
  'zIndex',
]);

const spacingProperties: Record<string, string[]> = {
  p: ['padding'],
  pt: ['paddingTop'],
  pr: ['paddingRight'],
  pb: ['paddingBottom'],
  pl: ['paddingLeft'],
  px: ['paddingLeft', 'paddingRight'],
  py: ['paddingTop', 'paddingBottom'],
  m: ['margin'],
  mt: ['marginTop'],
  mr: ['marginRight'],
  mb: ['marginBottom'],
  ml: ['marginLeft'],
  mx: ['marginLeft', 'marginRight'],
  my: ['marginTop', 'marginBottom'],
};

const convertCssValue = (
  key: string,
  value: string | number,
): string | number => {
  if (typeof value !== 'number') {
    return value;
  }

  if (
    spacingProperties[key] ||
    key === 'gap' ||
    key === 'columnGap' ||
    key === 'rowGap'
  ) {
    return spacingToPx(value);
  }

  if (key === 'borderRadius') {
    return radiusToPx(value);
  }

  if (unitlessCssProperties.has(key)) {
    return value;
  }

  return `${value}px`;
};

const sxToStyle = (sx?: SxStyle): React.CSSProperties => {
  if (!sx) {
    return {};
  }

  return Object.entries(sx).reduce<React.CSSProperties>(
    (style, [key, rawValue]) => {
      if (key.startsWith('&') || key.startsWith('.') || key.startsWith('@')) {
        return style;
      }

      const value = resolveResponsiveValue(rawValue as StyleValue);
      if (value === undefined || value === null) {
        return style;
      }

      const convertedValue = convertCssValue(key, value);
      const mappedProperties = spacingProperties[key] || [key];
      mappedProperties.forEach((property) => {
        (style as Record<string, string | number>)[property] = convertedValue;
      });
      return style;
    },
    {},
  );
};

const mergeStyles = (
  ...styles: Array<React.CSSProperties | undefined>
): React.CSSProperties => Object.assign({}, ...styles.filter(Boolean));

const cx = (...classes: Array<string | undefined | false>): string =>
  classes.filter(Boolean).join(' ');

type PrimitiveProps<T extends HTMLElement = HTMLElement> = {
  sx?: SxStyle;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<T>;

const Box = React.forwardRef<
  HTMLElement,
  PrimitiveProps & { component?: React.ElementType }
>(({ component = 'div', sx, style, className, children, ...props }, ref) => {
  const Component = component as any;
  return (
    <Component
      ref={ref}
      className={className}
      style={mergeStyles(sxToStyle(sx), style)}
      {...props}
    >
      {children}
    </Component>
  );
});
Box.displayName = 'Box';

type StackProps = PrimitiveProps<HTMLDivElement> & {
  direction?: StyleValue;
  spacing?: StyleValue;
  alignItems?: StyleValue;
  justifyContent?: StyleValue;
  flexWrap?: StyleValue;
  gap?: StyleValue;
  divider?: React.ReactNode;
  useFlexGap?: boolean;
};

const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'column',
      spacing,
      alignItems,
      justifyContent,
      flexWrap,
      gap,
      divider,
      useFlexGap: _useFlexGap,
      sx,
      style,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const childArray = React.Children.toArray(children).filter(Boolean);
    const content = divider
      ? childArray.flatMap((child, index) =>
          index === 0
            ? [child]
            : [
                React.cloneElement(divider as React.ReactElement, {
                  key: `divider-${index}`,
                }),
                child,
              ],
        )
      : childArray;

    return (
      <div
        ref={ref}
        className={cx('ce-stack', className)}
        style={mergeStyles(
          {
            flexDirection: resolveResponsiveValue(
              direction,
            ) as React.CSSProperties['flexDirection'],
            gap:
              resolveResponsiveValue(gap ?? spacing) !== undefined
                ? convertCssValue(
                    'gap',
                    resolveResponsiveValue(gap ?? spacing) as string | number,
                  )
                : undefined,
            alignItems: resolveResponsiveValue(
              alignItems,
            ) as React.CSSProperties['alignItems'],
            justifyContent: resolveResponsiveValue(
              justifyContent,
            ) as React.CSSProperties['justifyContent'],
            flexWrap: resolveResponsiveValue(
              flexWrap,
            ) as React.CSSProperties['flexWrap'],
          },
          sxToStyle(sx),
          style,
        )}
        {...props}
      >
        {content}
      </div>
    );
  },
);
Stack.displayName = 'Stack';

const Paper = React.forwardRef<HTMLDivElement, PrimitiveProps<HTMLDivElement>>(
  ({ sx, style, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('ce-paper', className)}
      style={mergeStyles(sxToStyle(sx), style)}
      {...props}
    >
      {children}
    </div>
  ),
);
Paper.displayName = 'Paper';

type TypographyProps = PrimitiveProps & {
  variant?: 'h6' | 'body2' | 'caption' | 'subtitle2' | 'overline';
  component?: React.ElementType;
};

const Typography: React.FC<TypographyProps> = ({
  variant,
  component,
  sx,
  style,
  className,
  children,
  ...props
}) => {
  const Component = (component || (variant === 'h6' ? 'h3' : 'p')) as any;
  const variantName = variant || 'default';
  return (
    <Component
      className={cx(
        'ce-typography',
        `ce-typography--${variantName}`,
        className,
      )}
      style={mergeStyles(sxToStyle(sx), style)}
      {...props}
    >
      {children}
    </Component>
  );
};

const CircularProgress: React.FC<{
  size?: number;
  color?: string;
  sx?: SxStyle;
}> = ({ size = 16, color, sx }) => (
  <span
    aria-hidden='true'
    className={cx('ce-spinner', color === 'inherit' && 'ce-spinner--inherit')}
    style={mergeStyles(
      // CSS custom properties aren't part of React.CSSProperties, so the
      // literal is cast to satisfy mergeStyles' typed parameter.
      { '--ce-spinner-size': `${size}px` } as React.CSSProperties,
      sxToStyle(sx),
    )}
  />
);

type ButtonProps = {
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small';
  startIcon?: React.ReactNode;
  disabled?: boolean;
  sx?: SxStyle;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const Button: React.FC<ButtonProps> = ({
  variant = 'text',
  size,
  startIcon,
  disabled,
  sx,
  style,
  className,
  children,
  ...props
}) => {
  return (
    <button
      type='button'
      disabled={disabled}
      className={cx(
        'ce-button',
        `ce-button--${variant}`,
        size === 'small' && 'ce-button--small',
        className,
      )}
      style={mergeStyles(sxToStyle(sx), style)}
      {...props}
    >
      {startIcon}
      {children}
    </button>
  );
};

const IconButton: React.FC<ButtonProps> = ({
  sx,
  style,
  className,
  children,
  disabled,
  ...props
}) => (
  <button
    type='button'
    disabled={disabled}
    className={cx('ce-icon-button', className)}
    style={mergeStyles(sxToStyle(sx), style)}
    {...props}
  >
    {children}
  </button>
);

type ChipProps = PrimitiveProps<HTMLSpanElement> & {
  label: React.ReactNode;
  color?: 'primary' | 'default' | 'success';
  variant?: 'outlined' | 'filled';
  size?: 'small';
  icon?: React.ReactNode;
};

const Chip: React.FC<ChipProps> = ({
  label,
  color = 'default',
  variant = 'filled',
  size,
  icon,
  sx,
  style,
  className,
  ...props
}) => (
  <span
    className={cx(
      'ce-chip',
      `ce-chip--${variant}`,
      `ce-chip--${color}`,
      size === 'small' && 'ce-chip--small',
      className,
    )}
    style={mergeStyles(sxToStyle(sx), style)}
    {...props}
  >
    {icon && <span className='ce-chip__icon'>{icon}</span>}
    {label}
  </span>
);

const Alert: React.FC<
  PrimitiveProps<HTMLDivElement> & {
    severity?: 'info' | 'error';
    icon?: React.ReactNode;
  }
> = ({ severity = 'info', icon, sx, style, children, ...props }) => {
  const isError = severity === 'error';
  return (
    <div
      role='status'
      className={cx('ce-alert', isError ? 'ce-alert--error' : 'ce-alert--info')}
      style={mergeStyles(sxToStyle(sx), style)}
      {...props}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
};

type TextFieldProps = {
  fullWidth?: boolean;
  multiline?: boolean;
  maxRows?: number;
  minRows?: number;
  size?: 'small';
  helperText?: React.ReactNode;
  sx?: SxStyle;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;

const TextField: React.FC<TextFieldProps> = ({
  fullWidth,
  multiline,
  maxRows,
  minRows,
  size: _size,
  helperText,
  sx,
  style,
  ...props
}) => {
  const inputStyle: React.CSSProperties = {
    minHeight: multiline ? undefined : 44,
    maxHeight: maxRows ? maxRows * 24 : undefined,
    resize: multiline ? 'vertical' : undefined,
  };
  const control = multiline ? (
    <textarea
      {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      className='ce-field__control'
      rows={minRows || 1}
      style={inputStyle}
    />
  ) : (
    <input {...props} className='ce-field__control' style={inputStyle} />
  );

  return (
    <label
      className={cx('ce-field', fullWidth && 'ce-field--full')}
      style={mergeStyles(sxToStyle(sx), style)}
    >
      {control}
      {helperText && <span className='ce-field__helper'>{helperText}</span>}
    </label>
  );
};

const Radio: React.FC<{
  checked?: boolean;
  tabIndex?: number;
  sx?: SxStyle;
}> = ({ checked, tabIndex, sx }) => (
  <span
    aria-hidden='true'
    tabIndex={tabIndex}
    className={cx('ce-radio', checked && 'ce-radio--checked')}
    style={sxToStyle(sx)}
  >
    <span className='ce-radio__dot' />
  </span>
);

const Divider: React.FC<{ flexItem?: boolean; sx?: SxStyle }> = ({ sx }) => (
  <hr className='ce-divider' style={sxToStyle(sx)} />
);

// Card surface matching EARChatUI's phase-result cards (.classify-res-comp
// in index.css: border-radius 8px, 2px border, var(--Background-200) fill,
// no shadow) so a result card rendered in this chat looks like the ones
// EARChatUI renders in its own chat bubbles.
const helperCardSx = {
  border: '2px solid var(--Background-400, #37393f)',
  borderRadius: '8px',
  backgroundColor: 'var(--Background-200, var(--bg-colors))',
  backgroundImage: 'none',
  boxShadow: 'none',
};

const modeCardSx = {
  width: '100%',
  p: 1.5,
  borderRadius: 2,
  border: '1px solid var(--Colors-Border-border-primary, #333741)',
  backgroundColor: 'var(--bg-primary)',
  cursor: 'pointer',
  outline: 'none',
  transition:
    'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    borderColor: 'var(--action-color)',
  },
};

const statusPillSx = {
  borderColor: 'rgba(34, 197, 94, 0.5)',
  backgroundColor: 'rgba(34, 197, 94, 0.12)',
  color: '#22c55e',
  fontWeight: 700,
};

const severityPillSx = {
  minHeight: 28,
  height: 28,
  padding: '3px 12px',
  borderRadius: 999,
  fontSize: '13px',
  fontWeight: 700,
  alignSelf: 'flex-start',
};

// Monospace, scrollable block for any raw JSON we still need to surface.
const codeBlockSx = {
  m: 0,
  p: 1.5,
  borderRadius: 1.5,
  border: '1px solid var(--es-soft-border, var(--border-color))',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary-color)',
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '13px',
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  overflow: 'auto',
  maxHeight: 360,
};

const isPlainObject = (value: any): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const KeyValueList: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const entries = Object.entries(data);
  if (!entries.length) {
    return (
      <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
        No details available.
      </Typography>
    );
  }

  return (
    <Stack
      divider={<Divider flexItem sx={{ borderColor: 'var(--border-color)' }} />}
    >
      {entries.map(([key, value]) => (
        <Stack
          key={key}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.25, sm: 2 }}
          sx={{ py: 0.9 }}
        >
          <Typography
            variant='body2'
            sx={{
              color: 'var(--text-secondary)',
              minWidth: 200,
              flexShrink: 0,
              textTransform: 'capitalize',
            }}
          >
            {key.replace(/[_-]+/g, ' ')}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              wordBreak: 'break-word',
              fontWeight: 500,
              color: 'var(--text-primary-color)',
            }}
          >
            {renderFieldWithLinks(
              value === null || value === undefined || value === ''
                ? '—'
                : isPlainObject(value) || Array.isArray(value)
                ? JSON.stringify(value)
                : String(value),
            )}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value, null, 2);
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) {
    return '0 KB';
  }

  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(kilobytes >= 10 ? 1 : 2)} KB`;
  }

  return `${(kilobytes / 1024).toFixed(2)} MB`;
};

const humanizeKey = (key: string): string =>
  key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

// Matches the "Thought for Ns" wording EARChatUI/BeneficialOwnershipWorkflow
// use for their live thinking indicator (standard-workflow-thought-summary).
const formatThoughtDuration = (seconds: number): string =>
  seconds < 2 ? 'Thought for a moment' : `Thought for ${seconds}s`;

const formatCompactValue = (value: any): string => {
  const formatted = formatValue(value);
  return formatted.length > 120 ? `${formatted.slice(0, 117)}...` : formatted;
};

const extractFirstValue = (source: any, keys: string[]): any => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  for (const key of keys) {
    if (
      source[key] !== undefined &&
      source[key] !== null &&
      source[key] !== ''
    ) {
      return source[key];
    }
  }

  return undefined;
};

const getDiscrepancySeverity = (item: any): 'Critical' | 'Warning' | 'Info' => {
  const rawSeverity = String(
    extractFirstValue(item, [
      'severity',
      'level',
      'risk',
      'category',
      'status',
    ]) || 'Info',
  ).toLowerCase();

  if (
    rawSeverity.includes('critical') ||
    rawSeverity.includes('high') ||
    rawSeverity.includes('mismatch')
  ) {
    return 'Critical';
  }

  if (
    rawSeverity.includes('warning') ||
    rawSeverity.includes('medium') ||
    rawSeverity.includes('review')
  ) {
    return 'Warning';
  }

  return 'Info';
};

const getDiscrepancySeverityColor = (severity: string): string => {
  const lowerSeverity = severity.toLowerCase();

  if (lowerSeverity.includes('critical')) {
    return '#ef4444';
  }

  if (lowerSeverity.includes('warning')) {
    return '#f59e0b';
  }

  return 'var(--action-color)';
};

const artifactPathFromS3Key = (s3Key: string): string => {
  const batchMatch = s3Key.match(/\/(docbatch_[^/]+)\//);
  if (!batchMatch) {
    return '';
  }

  return s3Key.slice(
    s3Key.indexOf(`/${batchMatch[1]}/`) + batchMatch[1].length + 2,
  );
};

const extractArtifacts = (source: any): Array<any> => {
  const mapArtifactUrls = (urlMap: Record<string, string>) =>
    Object.entries(urlMap)
      .filter(([, value]) => typeof value === 'string' && value.length > 0)
      .map(([key, value]) => {
        const filenameFromUrl = value.split('/').pop() || key;
        const marker = '/artifacts/';
        const markerIdx = value.indexOf(marker);
        const artifactPath =
          markerIdx >= 0
            ? value.slice(markerIdx + marker.length)
            : filenameFromUrl;
        return {
          key: key,
          filename: filenameFromUrl,
          path: artifactPath,
          download_url: value,
        };
      });

  const candidates = [
    source?.artifacts,
    source?.files,
    source?.results?.artifacts,
    source?.data?.artifacts,
    source?.downloads,
    source?.output_artifact_urls,
    source?.active_batch_manifest?.output_artifact_urls,
  ];

  const collected: any[] = [];
  const collectArtifactKeys = (value: any, keyName = '') => {
    if (!value || typeof value !== 'object') {
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      if (
        typeof nestedValue === 'string' &&
        key.endsWith('_key') &&
        nestedValue.includes('/docbatch_') &&
        nestedValue.includes('artifacts/')
      ) {
        const path = artifactPathFromS3Key(nestedValue);
        if (path) {
          collected.push({
            key: key,
            filename: path,
            path: path,
            s3_key: nestedValue,
          });
        }
      }
      if (nestedValue && typeof nestedValue === 'object' && keyName !== key) {
        collectArtifactKeys(nestedValue, key);
      }
    });
  };

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      collected.push(...candidate);
      continue;
    }
    if (candidate && typeof candidate === 'object') {
      const everyValueIsString = Object.values(candidate).every(
        (value) => typeof value === 'string',
      );
      if (everyValueIsString) {
        collected.push(...mapArtifactUrls(candidate as Record<string, string>));
      } else {
        const objectValues = Object.entries(candidate).map(([key, value]) => {
          if (value && typeof value === 'object') {
            return { key: key, ...value };
          }
          return { key: key, value: value };
        });
        collected.push(...objectValues);
      }
    }
  }
  collectArtifactKeys(source);

  const deduped = new Map<string, any>();
  collected.forEach((artifact, index) => {
    const id =
      artifact?.path ||
      artifact?.filename ||
      artifact?.file_name ||
      artifact?.fileName ||
      artifact?.download_url ||
      artifact?.downloadUrl ||
      `${artifact?.key || 'artifact'}-${index}`;
    if (!deduped.has(id)) {
      deduped.set(id, artifact);
    }
  });

  return Array.from(deduped.values());
};

const getCurrentUserId = (cur_user: any): string => {
  const candidates = [
    cur_user?.basics?.id,
    cur_user?.id,
    cur_user?.user_id,
    cur_user?.email,
  ];

  const match = candidates.find(
    (value) => value !== undefined && value !== null && value !== '',
  );
  return match !== undefined && match !== null ? String(match) : '';
};

const getBatchIdFromResponse = (response: any): string => {
  const batchId = extractFirstValue(response, [
    'document_batch_id',
    'documentBatchId',
    'batch_id',
    'batchId',
    'id',
  ]);

  return batchId ? String(batchId) : '';
};

const formatArtifactDisplayName = (rawLabel: string): string => {
  const basename = rawLabel.split('/').pop() || rawLabel;
  const lastDot = basename.lastIndexOf('.');
  const hasExtension = lastDot > 0 && lastDot < basename.length - 1;
  const namePart = hasExtension ? basename.slice(0, lastDot) : basename;
  const extension = hasExtension ? basename.slice(lastDot + 1) : '';

  const prettyName = namePart
    .replace(/_+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) =>
      word === word.toUpperCase()
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');

  if (!prettyName) {
    return basename;
  }

  return extension ? `${prettyName}.${extension}` : prettyName;
};

// A review-table row for the "Send to Import Hub" flow -- a plain Import
// Hub product row (see ImportServiceProduct in pages/import/lib/import-service.ts)
// plus local-only review state. `_client_row_id` is local to this table
// (not a database id) so a save result can be matched back to the row it
// came from -- a product's own `name` is not guaranteed unique within a
// batch. `_action` only ever needs 'create' or 'skip' here -- Import Hub's
// backend (a separate service, ProductServiceAPI) has no update-product
// endpoint at all, so "update the matching product" is not offered.
interface ImportHubReviewItem {
  _client_row_id: string;
  _action?: 'create' | 'skip';
  name: string;
  description?: string;
  category?: string;
  hierarchy_type?: string;
  hts_code?: string;
  country_of_origin?: string;
  manufacturer?: string;
  product_value?: number;
  quantity?: number;
  unit_of_measure?: string;
  model_number?: string;
  trans_method?: string;
  country_of_smelt_pour?: string;
  entry_date?: string;
  loading_date?: string;
  additional_columns?: Record<string, any>;
}

// Per-row outcome of a "Send to Import Hub" submit. No "updated" bucket --
// see ImportHubReviewItem's comment on why update isn't offered.
interface ImportHubResultItem {
  client_row_id: string;
  id?: string;
  name?: string;
  reason?: string;
}

interface ImportHubSendResult {
  created: ImportHubResultItem[];
  skipped: ImportHubResultItem[];
  failed: ImportHubResultItem[];
}

const IMPORT_HUB_CSV_FIELDS: Array<keyof ImportHubReviewItem> = [
  'model_number',
  'name',
  'description',
  'category',
  'hierarchy_type',
  'quantity',
  'unit_of_measure',
  'manufacturer',
  'country_of_origin',
  'hts_code',
  'trans_method',
  'country_of_smelt_pour',
  'entry_date',
  'loading_date',
  'product_value',
];

const importHubCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const escapeImportHubCsvValue = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const buildImportHubCsvUpload = (rows: ImportHubReviewItem[]) => {
  const standardHeaders = IMPORT_HUB_CSV_FIELDS.map(String);
  const standardHeaderSet = new Set(standardHeaders);
  const additionalHeaders: string[] = [];
  const seenAdditionalHeaders = new Set<string>();

  rows.forEach((row) => {
    Object.keys(row.additional_columns ?? {}).forEach((header) => {
      if (
        header &&
        !standardHeaderSet.has(header) &&
        !seenAdditionalHeaders.has(header)
      ) {
        seenAdditionalHeaders.add(header);
        additionalHeaders.push(header);
      }
    });
  });

  const resolvedHeaders = [...standardHeaders, ...additionalHeaders];
  const notMappedColumns = Object.fromEntries(
    additionalHeaders.map((header) => [header, header]),
  );
  const csvLines = [resolvedHeaders.map(escapeImportHubCsvValue).join(',')];

  rows.forEach((row) => {
    const values = [
      ...IMPORT_HUB_CSV_FIELDS.map((field) => importHubCsvValue(row[field])),
      ...additionalHeaders.map((header) =>
        importHubCsvValue(row.additional_columns?.[header]),
      ),
    ];
    csvLines.push(values.map(escapeImportHubCsvValue).join(','));
  });

  return {
    csvText: csvLines.join('\n'),
    resolvedHeaders: resolvedHeaders,
    notMappedColumns: notMappedColumns,
  };
};

const getCustomsLineIdentity = (row: {
  additional_columns?: Record<string, any> | null;
}): string => {
  const entryNumber = String(
    row.additional_columns?.['Entry Summary Number'] ||
      row.additional_columns?.['Entry Number'] ||
      '',
  ).trim();
  const lineNumber = String(
    row.additional_columns?.['Entry Summary Line Number'] || '',
  ).trim();
  const sourceIdentity = [
    entryNumber ? `Entry ${entryNumber}` : '',
    lineNumber ? `Line ${lineNumber}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return sourceIdentity || 'Customs line';
};

const addCustomsLineIdentity = (
  row: ImportHubReviewItem,
  collisionNumber = 1,
): ImportHubReviewItem => {
  const sourceIdentity = getCustomsLineIdentity(row);
  const suffix = `${sourceIdentity || 'Customs line'}${
    collisionNumber > 1 ? ` · ${collisionNumber}` : ''
  }`;
  const baseName = row.name.trim() || 'Unnamed product';
  const availableBaseLength = Math.max(1, 480 - suffix.length);

  return {
    ...row,
    name: `${baseName.slice(0, availableBaseLength)} — ${suffix}`,
  };
};

const canonicalImportHubValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isNaN(value) ? '' : String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^[\[{]/.test(trimmed)) {
      try {
        return canonicalImportHubValue(JSON.parse(trimmed));
      } catch {
        return trimmed.replace(/\s+/g, ' ').toLocaleLowerCase();
      }
    }
    return trimmed.replace(/\s+/g, ' ').toLocaleLowerCase();
  }
  if (Array.isArray(value)) return value.map(canonicalImportHubValue);
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nestedValue]) => [key, canonicalImportHubValue(nestedValue)]),
    );
  }
  return String(value);
};

const normalizeImportHubIdentityPart = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Entry + line is the durable identity of a Customs Analyzer product. It is
// deliberately preferred over comparing every display field: re-extracting
// the same 7501 may change analyzer provenance or harmless formatting, but it
// must never create a second copy of the same customs line in one database.
const getCustomsRecordIdentity = (row: {
  additional_columns?: Record<string, any> | string | null;
}): string | null => {
  let additionalColumns: Record<string, any> = {};
  if (typeof row.additional_columns === 'string') {
    try {
      const parsed = JSON.parse(row.additional_columns);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        additionalColumns = parsed;
      }
    } catch {
      additionalColumns = {};
    }
  } else if (row.additional_columns) {
    additionalColumns = row.additional_columns;
  }
  const entryNumber = normalizeImportHubIdentityPart(
    additionalColumns['Entry Summary Number'] || additionalColumns['Entry Number'],
  );
  const lineNumber = normalizeImportHubIdentityPart(
    additionalColumns['Entry Summary Line Number'],
  );
  return entryNumber && lineNumber
    ? `customs-entry-line::${entryNumber}::${lineNumber}`
    : null;
};

const getImportHubSourceName = (row: {
  name?: string | null;
  additional_columns?: Record<string, any> | null;
}): string => {
  const name = String(row.name || '').trim();
  const identity = getCustomsLineIdentity(row);
  const suffixStart = ` — ${identity}`;
  const suffixIndex = name.lastIndexOf(suffixStart);
  return suffixIndex > 0 ? name.slice(0, suffixIndex) : name;
};

const getImportHubProductFingerprint = (row: any): string =>
  JSON.stringify(
    canonicalImportHubValue({
      model_number: row.model_number,
      name: getImportHubSourceName(row),
      description: row.description,
      category: row.category,
      hierarchy_type: row.hierarchy_type,
      quantity: row.quantity,
      unit_of_measure: row.unit_of_measure,
      manufacturer: row.manufacturer,
      country_of_origin: row.country_of_origin,
      hts_code: row.hts_code,
      trans_method: row.trans_method,
      country_of_smelt_pour: row.country_of_smelt_pour,
      entry_date: row.entry_date,
      loading_date: row.loading_date,
      product_value: row.product_value,
      additional_columns: stripImportHubProvenance(row.additional_columns),
    }),
  );

const getImportHubDuplicateKey = (row: any): string =>
  getCustomsRecordIdentity(row) ||
  `record::${getImportHubProductFingerprint(row)}`;

const fetchAllImportProducts = async (databaseId: string) => {
  const products: any[] = [];
  let offset = 0;
  let total = 0;

  do {
    const page = await fetchImportProductsByDatabaseId(databaseId, {
      limit: 100,
      offset: offset,
      includeFacets: false,
    });
    if (!page.success) {
      throw new Error(
        page.error || 'Failed to verify existing products in this Import Hub database.',
      );
    }
    products.push(...page.products);
    total = page.total;
    offset += page.products.length;
    if (!page.products.length) break;
  } while (offset < total);

  return products;
};

// Real ES-003/CBP Form 7501 field labels (see pyapi's entry_parser.py
// LINE_FIELDS) -- used to build a complete, readably-labeled ES-003 record
// inside additional_columns, so opening a product in Import Hub shows the
// full customs entry line it came from, not just internal field names.
const ES003_FIELD_LABELS: Record<string, string> = {
  entry_number: 'Entry Number',
  full_entry_number: 'Entry Summary Number',
  entry_summary_line_number: 'Entry Summary Line Number',
  tariff_ordinal_number: 'Tariff Ordinal Number',
  htsus: 'HTS Number - Full',
  country_of_origin_code: 'Country of Origin Code',
  country_of_export_code: 'Country of Export Code',
  manufacturer_id: 'Manufacturer ID',
  entry_date: 'Entry Date',
  entry_summary_date: 'Entry Summary Date',
  import_date: 'Import Date',
  export_date: 'Export Date',
  port_of_entry_code: 'Port of Entry Code',
  importer_number: 'Importer Number',
  description: 'Description',
  commodity_desc: 'Commodity Description',
  relationship: 'Relationship',
  htsus_rate: 'Tariff Rate',
  duty_amount: 'Line Tariff Duty Amount',
  gross_weight: 'Gross Weight',
  manifest_qty: 'Manifest Quantity',
  net_quantity: 'Net Quantity',
  htsus_unit: 'Unit of Measure',
  entered_value: 'Line Tariff Goods Value Amount',
  chgs: 'Charges',
  mpf_amount: 'MPF Amount',
  po_number: 'PO Number',
  part_number: 'Part Number',
  document_batch_id: 'Source Batch ID',
  source_file: 'Source File',
  source_s3_key: 'Source File Storage Key',
};

// Import provenance -- which analyzer batch/upload produced this row -- is
// not part of the customs entry itself. Re-running the analyzer on the same
// document, or re-uploading a byte-identical PDF, mints a fresh
// document_batch_id/source_s3_key every time; left inside a duplicate-check
// fingerprint (see getImportHubProductFingerprint above), that makes an
// exact re-send of the same entry+line always look "new" and defeats
// duplicate detection entirely. Strip these before comparing -- everything
// else in additional_columns (Entry Number, Line Number, HTS, etc.) still
// has to match exactly.
const IMPORT_HUB_PROVENANCE_LABELS = new Set(
  [
    'document_batch_id',
    'source_file',
    'source_s3_key',
    ...['document_batch_id', 'source_file', 'source_s3_key'].map(
      (key) => ES003_FIELD_LABELS[key] || key,
    ),
  ],
);

// Recursive -- a line with attached "special program" tariff codes (Section
// 301/232, IEEPA) nests one ES-003 record per program line under
// additionalColumns['Special Program Tariffs'] (see mapEntryLinesToProducts),
// each carrying its own copy of the same provenance fields. A shallow strip
// would leave those nested copies free to vary between re-sends and still
// defeat the fingerprint match.
const stripImportHubProvenance = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripImportHubProvenance);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !IMPORT_HUB_PROVENANCE_LABELS.has(key))
        .map(([key, nestedValue]) => [key, stripImportHubProvenance(nestedValue)]),
    );
  }
  return value;
};

const buildEs003Fields = (line: any): Record<string, any> => {
  const fields: Record<string, any> = {};
  Object.keys(line).forEach((key) => {
    if (line[key] === undefined || line[key] === '') return;
    fields[ES003_FIELD_LABELS[key] || key] = line[key];
  });
  return fields;
};

// Maps parsed customs entry lines onto Import Hub product rows (see
// ImportServiceProduct in pages/import/lib/import-service.ts). A single
// physical 7501 line item can produce MULTIPLE parsed rows -- one per HTS
// code attached to it (see entry_parser.py's parse_line_items): a base
// merchandise HTS (always the highest tariff_ordinal_number) plus zero or
// more "special program" codes (Section 301/232, IEEPA, etc. -- ordinals
// 1..N-1) that adjust duty on that SAME item. Those program rows share the
// exact same entered_value/net_quantity as the base row -- they are not
// separate products. Sending them as their own products would both
// triple-count the line's value and fail outright (their HTS codes are
// 8-digit program codes, not real 10-digit merchandise codes Import Hub's
// product-service requires). So: group by line number, use the
// merchandise row as the actual product, and fold every program row's own
// ES-003 fields into that product's "Special Program Tariffs" list.
// Number(...) alone can't tell "genuinely missing" apart from "genuinely zero" --
// Number(undefined) is already NaN, but the field's own `|| 0` fallback used to
// collapse that (and any non-numeric string) down to a plain 0, which reads as
// valid data to getProductRowIssues' Number.isNaN check. Only fall through to
// Number() once undefined/null/'' are ruled out, so those stay NaN (and get
// flagged) while a real 0 still reads as 0.
const toNumericOrNaN = (value: unknown): number =>
  value === undefined || value === null || value === '' ? Number.NaN : Number(value);

const getFirstCustomsValue = (
  row: Record<string, any>,
  keys: string[],
): string => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
};

const normalizeCustomsDateForImport = (value: unknown): string | undefined => {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!usMatch) return undefined;
  return `${usMatch[3]}-${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}`;
};

// Versioned facts-only contract for Import Hub screening. This deliberately
// contains no computed flags or compliance decisions: Customs Analyzer owns
// extraction and source relationships; Import Hub owns screening.
const buildImportHubScreeningContext = (
  primary: Record<string, any>,
  programLines: Array<Record<string, any>>,
) => {
  const entryNumber = getFirstCustomsValue(primary, [
    'full_entry_number',
    'entry_number',
  ]);
  const lineNumber = getFirstCustomsValue(primary, [
    'entry_summary_line_number',
    'line_number',
  ]);
  const smeltPourCountry = getFirstCustomsValue(primary, [
    'country_of_smelt_pour',
    'country_of_melt_pour',
    'country_of_melt_and_pour',
    'smelt_pour_country',
  ]);
  const loadingDate = normalizeCustomsDateForImport(
    getFirstCustomsValue(primary, ['loading_date', 'export_date']),
  );
  const reconciliationValue = getFirstCustomsValue(primary, [
    'value_reconciliation',
    'value_reconciliation_flag',
    'reconciliation_status',
  ]);

  return {
    schema_version: 1,
    source: {
      tool: 'customs_entry_analyzer',
      entry_number: entryNumber,
      line_number: lineNumber,
      document_batch_id: getFirstCustomsValue(primary, ['document_batch_id']),
      source_file: getFirstCustomsValue(primary, ['source_file']),
      source_s3_key: getFirstCustomsValue(primary, ['source_s3_key']),
    },
    product: {
      part_number: getFirstCustomsValue(primary, ['part_number']),
      description: getFirstCustomsValue(primary, [
        'commodity_desc',
        'description',
      ]),
      base_hts_code: getFirstCustomsValue(primary, ['htsus']),
      country_of_origin: getFirstCustomsValue(primary, [
        'country_of_origin_code',
      ]),
      manufacturer_id: getFirstCustomsValue(primary, ['manufacturer_id']),
      entered_value: primary.entered_value ?? null,
      quantity: primary.net_quantity ?? null,
      unit_of_measure: getFirstCustomsValue(primary, ['htsus_unit']),
    },
    classification_inputs: {
      entry_date: normalizeCustomsDateForImport(primary.entry_date) ?? null,
      entry_summary_date:
        normalizeCustomsDateForImport(primary.entry_summary_date) ?? null,
      loading_date: loadingDate ?? null,
      country_of_export: getFirstCustomsValue(primary, [
        'country_of_export_code',
      ]),
      country_of_smelt_pour: smeltPourCountry || null,
      chapter_98_99_codes: programLines.map((line) => ({
        code: getFirstCustomsValue(line, ['htsus']),
        tariff_ordinal_number: line.tariff_ordinal_number ?? null,
        rate: line.htsus_rate ?? null,
        duty_amount: line.duty_amount ?? null,
        entered_value: line.entered_value ?? null,
        country_of_origin: line.country_of_origin_code ?? null,
        raw_es003_fields: buildEs003Fields(line),
      })),
    },
    reconciliation: {
      value_reconciliation: reconciliationValue || null,
    },
    raw_es003_fields: buildEs003Fields(primary),
  };
};

const mapEntryLinesToProducts = (lines: any[]): ImportHubReviewItem[] => {
  const groups = new Map<string, any[]>();
  lines.forEach((line, index) => {
    // Group by line number alone when it's missing -- entry_summary_line_number
    // is the parser's own per-block key (see entry_parser.py's LINE_START), so
    // it's never blank for a real line; full_entry_number/entry_number being
    // blank too (e.g. the header failed to parse) doesn't risk collisions as
    // long as the line number still disambiguates. Only fall back to a unique
    // key when the line number itself is missing, so genuinely distinct rows
    // never collapse into one product -- and rows that do share a real line
    // number (a line's own base + trade-remedy codes) still group together.
    const lineNumber = line.entry_summary_line_number;
    const key =
      lineNumber === undefined || lineNumber === null || lineNumber === ''
        ? `ungrouped::${index}`
        : `${line.full_entry_number || line.entry_number || ''}::${lineNumber}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(line);
  });

  const rows: ImportHubReviewItem[] = [];
  groups.forEach((groupLines) => {
    const sorted = [...groupLines].sort(
      (a, b) => (Number(a.tariff_ordinal_number) || 0) - (Number(b.tariff_ordinal_number) || 0),
    );
    const primary = sorted[sorted.length - 1];
    const programLines = sorted.slice(0, -1);

    const partNumber = String(primary.part_number || '').trim();
    const description = String(primary.commodity_desc || primary.description || '').trim();
    const name = [partNumber, description].filter(Boolean).join(' — ').slice(0, 120)
      || `Line ${primary.entry_summary_line_number || ''}`.trim()
      || 'Unnamed product';

    const additionalColumns: Record<string, any> = buildEs003Fields(primary);
    // net_quantity is sometimes fractional (e.g. a weight-based unit like
    // 0.03). Import Hub's `quantity` column is a bigint -- whole numbers
    // only -- so round defensively, and keep the exact original value
    // visible so it isn't silently lost.
    const rawNetQuantity = Number(primary.net_quantity);
    if (Number.isFinite(rawNetQuantity) && !Number.isInteger(rawNetQuantity)) {
      additionalColumns['Net Quantity (exact)'] = primary.net_quantity;
    }
    if (programLines.length) {
      additionalColumns['Special Program Tariffs'] = programLines.map(buildEs003Fields);
    }
    additionalColumns.screening_context = buildImportHubScreeningContext(
      primary,
      programLines,
    );

    const entryDate = normalizeCustomsDateForImport(primary.entry_date);
    const loadingDate = normalizeCustomsDateForImport(
      getFirstCustomsValue(primary, ['loading_date', 'export_date']),
    );
    const smeltPourCountry = getFirstCustomsValue(primary, [
      'country_of_smelt_pour',
      'country_of_melt_pour',
      'country_of_melt_and_pour',
      'smelt_pour_country',
    ]);

    rows.push({
      _client_row_id: `row-${rows.length}`,
      name: name,
      description: description || name,
      // No `category` -- Import Hub rejects any category that isn't
      // already one of the team's own product categories, and a made-up
      // one like "Customs Entry Line" never will be. Leaving it unset
      // skips that check entirely instead of guaranteeing a rejection.
      hierarchy_type: 'Assembly',
      hts_code: String(primary.htsus || '').trim(),
      country_of_origin: String(primary.country_of_origin_code || '').trim(),
      // CBP's Manufacturer/Shipper ID (MID) code -- a 7501 line carries no
      // friendlier manufacturer name than this.
      manufacturer: String(primary.manufacturer_id || '').trim() || undefined,
      product_value: toNumericOrNaN(primary.entered_value),
      quantity: Math.round(toNumericOrNaN(primary.net_quantity)),
      unit_of_measure: String(primary.htsus_unit || '').trim() || undefined,
      model_number: partNumber,
      trans_method:
        getFirstCustomsValue(primary, ['trans_method', 'mode_of_transport']) ||
        undefined,
      country_of_smelt_pour: smeltPourCountry || undefined,
      entry_date: entryDate,
      loading_date: loadingDate,
      additional_columns: additionalColumns,
    });
  });

  return rows;
};

// Client-side checks the review screen runs on every row before it's ever
// sent anywhere -- catches obviously-broken rows (a blank required field, a
// negative dollar amount) immediately, on top of whatever the server's own
// duplicate/validation check reports back once "Check for duplicates" runs.
const getProductRowIssues = (row: ImportHubReviewItem): string[] => {
  const issues: string[] = [];
  if (!row.name?.trim()) issues.push('Missing product name.');
  if (!row.hts_code?.trim()) issues.push('Missing HTS code.');
  if (!row.country_of_origin?.trim()) issues.push('Missing country of origin.');
  if (typeof row.product_value !== 'number' || Number.isNaN(row.product_value) || row.product_value < 0) {
    issues.push('Value looks wrong.');
  }
  if (typeof row.quantity !== 'number' || Number.isNaN(row.quantity) || row.quantity < 0) {
    issues.push('Quantity looks wrong.');
  }
  return issues;
};

// Matches createProduct's exact rejection text (product.controller.ts) for
// an HTS code that isn't in the schedule as effective TODAY -- a historical
// 7501's classification can be perfectly correct for its filing date and
// still hit this, since the product-service only checks "current". Distinct
// from every other createProduct validation error (missing name, bad
// country, unknown category, etc.), which no HTS lookup can fix.
const isHtsScheduleFailure = (reason?: string): boolean =>
  /HTS code/i.test(reason || '') && /current USHTS schedule/i.test(reason || '');

const resolveArtifactLabel = (artifact: any, index: number): string => {
  const label =
    artifact?.file_name ||
    artifact?.fileName ||
    artifact?.name ||
    artifact?.path ||
    artifact?.filename ||
    artifact?.key;

  return label ? formatArtifactDisplayName(String(label)) : `Artifact ${index + 1}`;
};

const extractArtifactPath = (artifact: any): string => {
  const directPath =
    artifact?.path ||
    artifact?.file_name ||
    artifact?.fileName ||
    artifact?.filename ||
    '';
  if (directPath) {
    return String(directPath);
  }

  const url =
    artifact?.download_url || artifact?.downloadUrl || artifact?.url || '';
  if (typeof url !== 'string' || !url) {
    return '';
  }

  const marker = '/artifacts/';
  const idx = url.indexOf(marker);
  if (idx >= 0) {
    return url.slice(idx + marker.length);
  }

  return '';
};

const isDownloadableArtifact = (artifact: any): boolean => {
  if (!artifact || typeof artifact !== 'object') {
    return false;
  }
  if (extractArtifactPath(artifact)) {
    return true;
  }
  const directUrl =
    artifact?.download_url || artifact?.downloadUrl || artifact?.url;
  return typeof directUrl === 'string' && directUrl.length > 0;
};

const toStringArray = (value: any): string[] => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }
  return [String(value)];
};

// ---------------------------------------------------------------------------
// Chat message model. A single running conversation now carries three kinds
// of bot turns: transient `status` progress lines while a batch is
// uploading/analyzing, an `assistant` turn whose `resultPayload` renders the
// full analysis-result card inline (AnalysisResultCard below), and plain
// `assistant`/`error` text turns for the ask-the-agent Q&A (still talking
// directly to the existing /pyapi/customs/agent endpoint).
// ---------------------------------------------------------------------------

type ChatDownloadLink = {
  label: string;
  path: string;
};

type AnalysisResultPayload = {
  analysisResponse: any;
  analysisMode: string;
  documentBatchId: string;
  artifactList: any[];
  es003Diff?: any;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'error' | 'status';
  text: string;
  // Accumulated step lines for a 'status' message (e.g. "Uploading PDF…",
  // "Upload complete. Running Full comparison…") -- rendered in full inside
  // the Thinking card instead of only ever showing the latest one. `text`
  // is kept in sync with the last entry so any older code path that reads
  // `.text` directly still sees the current status.
  steps?: string[];
  downloadLinks?: ChatDownloadLink[];
  resultPayload?: AnalysisResultPayload;
};

const STARTER_PROMPTS = [
  {
    label: 'Summarize the discrepancies',
    prompt: 'Summarize the discrepancies in this document.',
  },
  {
    label: 'What HTS codes are on this entry?',
    prompt:
      'What HTS codes are on this entry? Include any line item context you have.',
  },
  {
    label: 'Why is line 11 flagged?',
    prompt:
      'Why is line 11 flagged? Explain the issue and what documents or values support it.',
  },
  {
    label: "What's the total declared value?",
    prompt: "What's the total declared value for this document?",
  },
  {
    label: 'Which documents are missing?',
    prompt: 'Which documents are missing?',
  },
];

const msgId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Opt-out for the "confirm before leaving" guard below -- same
// localStorage-only pattern BeneficialOwnershipWorkflow.tsx uses (Tariff's
// own version also persists to a user profile attribute via UsersAPI, which
// would pull in a server round trip this page doesn't otherwise need).
const LEAVE_WARNING_STORAGE_KEY = 'customsEntryAnalyzerLeaveWarningDismissed';

// ---------------------------------------------------------------------------
// AnalysisResultCard — renders one completed batch's results (parsed
// summary, discrepancy check, missing documents, artifacts, send-to-BITE)
// inline inside a chat bubble. Derivations and markup are the same ones the
// page used to render as a standalone "results" section; they now read from
// this message's own frozen `payload` instead of page-level state, so older
// runs in the same conversation keep showing their own results untouched by
// a later run.
// ---------------------------------------------------------------------------

const AnalysisResultCard: React.FC<{
  payload: AnalysisResultPayload;
  isLoadingArtifacts: boolean;
  // Only the most recent completed run in the conversation defaults open --
  // older runs collapse behind "View full breakdown" so the thread reads as
  // a conversation instead of a stack of dashboards. A user who opens an
  // older one keeps it open across re-renders since this only sets the
  // *initial* state of the card's own toggle, below.
  isLatest: boolean;
  onDownloadArtifact: (artifact: any, index: number) => void;
  onDownloadAllArtifacts: (artifacts: any[]) => void;
  onRefreshArtifacts: () => void;
  onRunEs003Diff: (file: File) => Promise<any>;
  onSendToTool: (toolName: string, payload: AnalysisResultPayload) => void;
  onRevealTryAsking: () => void;
}> = ({
  payload,
  isLoadingArtifacts,
  isLatest,
  onDownloadArtifact,
  onDownloadAllArtifacts,
  onRefreshArtifacts,
  onRunEs003Diff,
  onSendToTool,
  onRevealTryAsking,
}) => {
  const { analysisResponse, analysisMode, documentBatchId, artifactList, es003Diff } =
    payload;

  const [isExpanded, setIsExpanded] = useState(isLatest);
  // "Compare vs ACE ES-003" is one of the "what would you like to do next"
  // options below (same pattern EARChatUI/ExportPage uses) -- its own upload
  // UI only mounts once the user actually picks it, instead of always taking
  // up space in the breakdown.
  const [showEs003Section, setShowEs003Section] = useState(false);

  const [es003File, setEs003File] = useState<File | null>(null);
  const [es003Error, setEs003Error] = useState<string | null>(null);
  const [isDiffingEs003, setIsDiffingEs003] = useState(false);
  const es003FileInputRef = useRef<HTMLInputElement | null>(null);

  const handleEs003FileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) {
      return;
    }

    const isCsvOrExcel =
      /\.(csv|xlsx|xlsm)$/i.test(file.name) ||
      [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ].includes(file.type);
    if (!isCsvOrExcel) {
      setEs003Error('Only .csv or .xlsx ACE ES-003 exports are supported.');
      return;
    }

    setEs003Error(null);
    setEs003File(file);
  };

  const handleClearEs003File = () => {
    setEs003File(null);
    setEs003Error(null);
    if (es003FileInputRef.current) {
      es003FileInputRef.current.value = '';
    }
  };

  const handleRunEs003DiffClick = async () => {
    if (!es003File) {
      return;
    }

    setIsDiffingEs003(true);
    setEs003Error(null);
    try {
      await onRunEs003Diff(es003File);
    } catch (diffError) {
      const message =
        diffError instanceof Error
          ? diffError.message
          : 'Failed to compare the ACE ES-003 export.';
      setEs003Error(message);
    } finally {
      setIsDiffingEs003(false);
    }
  };

  // Fields with real signal (a mismatch or a presence gap on either side)
  // surface first -- a field that's just a clean 1:1 match everywhere is
  // the least useful thing to scroll past first in a field-presence report.
  const es003FieldRows = es003Diff?.fields
    ? Object.entries(es003Diff.fields as Record<string, any>)
        .map(([field, stats]) => ({
          field: field,
          stats: stats,
          mapping:
            es003Diff.field_mapping?.[field] ||
            { ace_column: null, confidence: 'absent', score: null },
        }))
        .sort((a, b) => {
          const signal = (row: any) =>
            (row.stats.mismatch || 0) * 3 +
            (row.stats.missing_in_ours || 0) +
            (row.stats.missing_in_ace || 0);
          return signal(b) - signal(a);
        })
    : [];

  const primaryResult =
    analysisResponse?.results?.[0] || analysisResponse?.result || null;

  const entryNumber =
    extractFirstValue(primaryResult, [
      'full_entry_number',
      'fullEntryNumber',
      'entry_number',
      'entryNumber',
    ]) ||
    (analysisMode === 'analyze'
      ? extractFirstValue(primaryResult?.parse, [
          'full_entry_number',
          'fullEntryNumber',
          'entry_number',
          'entryNumber',
        ])
      : undefined) ||
    extractFirstValue(analysisResponse, [
      'entry_number',
      'entryNumber',
      'entry_no',
      'entryNo',
      'summary_entry_number',
    ]) ||
    extractFirstValue(primaryResult, [
      'entry_number',
      'entryNumber',
      'entry_no',
      'entryNo',
      'full_entry_number',
      'fullEntryNumber',
    ]);

  const missingDocuments =
    analysisMode === 'package-check'
      ? toStringArray(
          extractFirstValue(analysisResponse, [
            'missing_documents',
            'missingDocuments',
          ]) ||
            extractFirstValue(primaryResult, [
              'missing_documents',
              'missingDocuments',
            ]),
        )
      : [];

  const parsedEntrySummary =
    analysisMode === 'ingest'
      ? extractFirstValue(analysisResponse, [
          'parsed_entry_summary',
          'parsedEntrySummary',
          'parsed_entry',
          'parsedEntry',
          'entry_summary',
          'entrySummary',
        ]) ||
        extractFirstValue(primaryResult, [
          'header_summary',
          'headerSummary',
          'summary',
        ])
      : analysisMode === 'analyze'
      ? extractFirstValue(primaryResult?.parse, [
          'header_summary',
          'headerSummary',
        ]) || primaryResult?.parse
      : undefined;

  const discrepancyDetails =
    analysisMode === 'analyze'
      ? extractFirstValue(primaryResult, [
          'discrepancies',
          'discrepancy_details',
          'discrepancyDetails',
        ]) ||
        extractFirstValue(analysisResponse, [
          'discrepancy_details',
          'discrepancyDetails',
          'discrepancies',
        ]) ||
        extractFirstValue(analysisResponse?.comparison, [
          'discrepancies',
          'details',
        ])
      : undefined;

  const discrepancyCount =
    analysisMode === 'analyze'
      ? extractFirstValue(primaryResult?.discrepancies, ['count']) ||
        extractFirstValue(analysisResponse, [
          'discrepancy_count',
          'discrepancyCount',
        ]) ||
        extractFirstValue(analysisResponse?.comparison, [
          'discrepancy_count',
          'discrepancyCount',
          'count',
        ]) ||
        (Array.isArray(discrepancyDetails?.discrepancies)
          ? discrepancyDetails.discrepancies.length
          : Array.isArray(discrepancyDetails)
          ? discrepancyDetails.length
          : undefined)
      : undefined;

  const downloadableArtifacts = artifactList.filter((artifact) =>
    isDownloadableArtifact(artifact),
  );
  const selectedMode = ANALYSIS_MODES.find(
    (mode) => mode.value === analysisMode,
  );
  const selectedModeLabel = selectedMode?.label || analysisMode;
  const parsedSummaryObject = isPlainObject(parsedEntrySummary)
    ? parsedEntrySummary
    : {};
  const parsedSummaryCards = [
    {
      label: 'Entry number',
      value:
        extractFirstValue(parsedSummaryObject, [
          'entry_number',
          'entryNumber',
          'entry_no',
          'entryNo',
          'summary_entry_number',
          'summaryEntryNumber',
        ]) || entryNumber,
    },
    {
      label: 'Full entry number',
      value: extractFirstValue(parsedSummaryObject, [
        'full_entry_number',
        'fullEntryNumber',
        'complete_entry_number',
        'completeEntryNumber',
      ]),
    },
    {
      label: 'Summary date',
      value: extractFirstValue(parsedSummaryObject, [
        'summary_date',
        'summaryDate',
        'entry_summary_date',
        'entrySummaryDate',
      ]),
    },
    {
      label: 'Entry date',
      value: extractFirstValue(parsedSummaryObject, [
        'entry_date',
        'entryDate',
        'date_of_entry',
        'dateOfEntry',
      ]),
    },
    {
      label: 'Importing carrier',
      value: extractFirstValue(parsedSummaryObject, [
        'importing_carrier',
        'importingCarrier',
        'carrier',
        'carrier_name',
        'carrierName',
      ]),
    },
    {
      label: 'Country of origin',
      value: extractFirstValue(parsedSummaryObject, [
        'country_of_origin',
        'countryOfOrigin',
        'origin_country',
        'originCountry',
      ]),
    },
    {
      label: 'Total entered value',
      value: extractFirstValue(parsedSummaryObject, [
        'total_entered_value',
        'totalEnteredValue',
        'entered_value',
        'enteredValue',
      ]),
    },
    {
      label: 'Duty',
      value: extractFirstValue(parsedSummaryObject, [
        'duty',
        'duty_amount',
        'dutyAmount',
      ]),
    },
    {
      label: 'Total',
      value: extractFirstValue(parsedSummaryObject, [
        'total',
        'total_amount',
        'totalAmount',
      ]),
    },
    {
      label: 'Importer of record',
      value: extractFirstValue(parsedSummaryObject, [
        'importer_of_record',
        'importerOfRecord',
        'importer',
        'importer_name',
        'importerName',
      ]),
    },
  ].filter(
    (item) =>
      item.value !== undefined && item.value !== null && item.value !== '',
  );
  const fallbackSummaryCards = Object.entries(parsedSummaryObject)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    )
    .slice(0, 10)
    .map(([key, value]) => ({ label: humanizeKey(key), value: value }));
  const visibleSummaryCards =
    parsedSummaryCards.length > 1 ? parsedSummaryCards : fallbackSummaryCards;
  const rawDiscrepancyRows = Array.isArray(discrepancyDetails)
    ? discrepancyDetails
    : Array.isArray(discrepancyDetails?.discrepancies)
    ? discrepancyDetails.discrepancies
    : Array.isArray(discrepancyDetails?.items)
    ? discrepancyDetails.items
    : [];
  const discrepancyRows = rawDiscrepancyRows.map((item: any) =>
    isPlainObject(item) ? item : { description: item },
  );
  const severityCounts = discrepancyRows.reduce(
    (
      counts: { critical: number; warning: number; info: number },
      item: any,
    ) => {
      const severity = getDiscrepancySeverity(item);
      if (severity === 'Critical') {
        counts.critical += 1;
      } else if (severity === 'Warning') {
        counts.warning += 1;
      } else {
        counts.info += 1;
      }
      return counts;
    },
    { critical: 0, warning: 0, info: 0 },
  );
  const displayedDiscrepancyCount =
    Number(discrepancyCount ?? discrepancyRows.length) ||
    discrepancyRows.length;
  const showDiscrepancyCheck =
    analysisMode === 'analyze' &&
    (displayedDiscrepancyCount > 0 ||
      discrepancyRows.length > 0 ||
      Boolean(discrepancyDetails));

  // A one-line, plain-language stand-in for the reply text -- the actual
  // chat "message" from the assistant's point of view. The full breakdown
  // below (parsed fields, discrepancy grid, artifacts, ES-003) is detail the
  // user can open on demand, not the reply itself.
  const summaryLine = (() => {
    if (analysisMode === 'package-check') {
      return missingDocuments.length > 0
        ? `Checked the package — ${missingDocuments.length} document${missingDocuments.length === 1 ? '' : 's'} missing.`
        : 'Checked the package — all required documents are present.';
    }
    const discrepancyPart =
      analysisMode === 'analyze'
        ? displayedDiscrepancyCount > 0
          ? `${displayedDiscrepancyCount} discrepanc${displayedDiscrepancyCount === 1 ? 'y' : 'ies'} found`
          : 'no discrepancies found'
        : null;
    const artifactsPart =
      downloadableArtifacts.length > 0
        ? `${downloadableArtifacts.length} artifact${downloadableArtifacts.length === 1 ? '' : 's'} ready to download`
        : null;
    const parts = [discrepancyPart, artifactsPart].filter(Boolean);
    const verb = analysisMode === 'analyze' ? 'Compared the entry' : 'Parsed the entry';
    return parts.length ? `${verb} — ${parts.join(', ')}.` : `${verb}.`;
  })();

  // "What would you like to do next?" -- same numbered-options pattern
  // EARChatUI/ExportPage use for their post-result next steps (see
  // .ce-next-step-options in standard-workflow.css). "Send to Import Hub" is
  // the one real send integration; ACE Analyzer and Tariff Analyzer aren't
  // offered here -- this tool only hands data to Import Hub. Compare vs ACE
  // ES-003 is real and reveals its own section. Both are gated on
  // analysisMode !== 'package-check' -- Package checker never extracts/writes
  // product_rows.json or artifact_keys (see analyzer_service.package_check_
  // document_batch), so there's nothing for either flow to send or diff.
  const nextStepOptions = [
    ...(analysisMode !== 'package-check'
      ? [
          {
            key: 'import-hub',
            label: 'Send to Import Hub',
            toolName: 'Import Hub',
            type: 'send' as const,
          },
          { key: 'es003', label: 'Compare vs ACE ES-003', type: 'reveal-es003' as const },
        ]
      : []),
    { key: 'ask', label: 'Ask about this analysis', type: 'reveal-ask' as const },
  ];

  const handleNextStepOptionClick = (option: (typeof nextStepOptions)[number]) => {
    if (option.type === 'reveal-es003') {
      setShowEs003Section((value) => !value);
      return;
    }
    if (option.type === 'reveal-ask') {
      onRevealTryAsking();
      return;
    }
    onSendToTool(option.toolName, payload);
  };

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap' useFlexGap>
        <Chip variant='outlined' label='Complete' sx={statusPillSx} />
        <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
          {selectedModeLabel} · {documentBatchId}
        </Typography>
      </Stack>

      <Typography sx={{ fontSize: '15px', lineHeight: 1.5 }} data-testid='result-summary-line'>
        {summaryLine}
      </Typography>

      <Button
        variant='text'
        onClick={() => setIsExpanded((value) => !value)}
        sx={{ alignSelf: 'flex-start', px: 0, color: 'var(--action-color)' }}
        data-testid='toggle-breakdown-button'
      >
        {isExpanded ? 'Hide full breakdown' : 'View full breakdown'}
        {isExpanded ? (
          <ExpandLessOutlinedIcon fontSize='small' />
        ) : (
          <ExpandMoreOutlinedIcon fontSize='small' />
        )}
      </Button>

      {isExpanded && (
      <>
        <Paper sx={{ p: { xs: 2, md: 2.5 }, ...helperCardSx }}>
          <Stack spacing={2}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              spacing={1}
            >
              <Typography variant='h6'>Parsed entry summary</Typography>
              <Chip variant='outlined' label='Parsed' sx={statusPillSx} />
            </Stack>
            {visibleSummaryCards.length ? (
              <Stack divider={<Divider sx={{ borderColor: 'var(--border-color)' }} />} spacing={1.25}>
                {visibleSummaryCards.map((item) => (
                  <Box key={item.label}>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontSize: '10px',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        color: 'var(--text-primary-color)',
                        fontSize: '14px',
                        lineHeight: 1.25,
                      }}
                    >
                      {renderFieldWithLinks(formatCompactValue(item.value))}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : isPlainObject(parsedEntrySummary) ? (
              <KeyValueList data={parsedEntrySummary} />
            ) : parsedEntrySummary ? (
              <Box component='pre' sx={codeBlockSx}>
                {renderFieldWithLinks(formatValue(parsedEntrySummary))}
              </Box>
            ) : (
              <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                No parsed summary fields returned.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Stack
          direction='column'
          spacing={showDiscrepancyCheck ? 2.5 : 0}
          alignItems='flex-start'
        >
          <Stack
            spacing={2.5}
            sx={{
              display: showDiscrepancyCheck ? 'flex' : 'none',
              minWidth: 0,
              width: '100%',
            }}
          >
            {showDiscrepancyCheck && (
              <Paper
                sx={{
                  p: { xs: 2.25, md: 3 },
                  height: 'auto',
                  overflowY: 'visible',
                  ...helperCardSx,
                }}
              >
                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent='space-between'
                    alignItems='flex-start'
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant='h6'>Discrepancy check</Typography>
                      <Typography
                        sx={{
                          color:
                            displayedDiscrepancyCount > 0
                              ? '#ef4444'
                              : '#22c55e',
                          fontWeight: 800,
                          fontSize: '19px',
                          mt: 2,
                        }}
                      >
                        {displayedDiscrepancyCount > 0
                          ? `${displayedDiscrepancyCount} discrepancies found`
                          : 'No discrepancies found'}
                      </Typography>
                    </Box>
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                      {severityCounts.critical > 0 && (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={`${severityCounts.critical} Critical`}
                          sx={{
                            ...severityPillSx,
                            color: '#ef4444',
                            borderColor: 'rgba(239, 68, 68, 0.45)',
                          }}
                        />
                      )}
                      {severityCounts.warning > 0 && (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={`${severityCounts.warning} Warning`}
                          sx={{
                            ...severityPillSx,
                            color: '#f59e0b',
                            borderColor: 'rgba(245, 158, 11, 0.45)',
                          }}
                        />
                      )}
                      {severityCounts.info > 0 && (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={`${severityCounts.info} Info`}
                          sx={{
                            ...severityPillSx,
                            color: 'var(--action-color)',
                            borderColor: 'rgba(3, 169, 244, 0.45)',
                          }}
                        />
                      )}
                    </Stack>
                  </Stack>

                  {discrepancyRows.length ? (
                    <Box
                      sx={{
                        border: '1px solid var(--es-soft-border)',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: '140px minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1fr)',
                          },
                          gap: 0,
                          backgroundColor: 'var(--bg-primary)',
                        }}
                      >
                        {[
                          'Severity',
                          'Type & description',
                          'Field / document',
                          'Expected → found',
                        ].map((header) => (
                          <Typography
                            key={header}
                            variant='caption'
                            sx={{
                              display: { xs: 'none', md: 'block' },
                              px: 1.5,
                              py: 1.25,
                              color: 'var(--text-secondary)',
                              textTransform: 'uppercase',
                              fontWeight: 800,
                              borderBottom: '1px solid var(--es-soft-border)',
                            }}
                          >
                            {header}
                          </Typography>
                        ))}
                        {discrepancyRows.map((item: any, index: number) => {
                          const severity = getDiscrepancySeverity(item);
                          const severityColor =
                            getDiscrepancySeverityColor(severity);
                          const title = extractFirstValue(item, [
                            'field',
                            'type',
                            'title',
                            'issue',
                            'name',
                            'description',
                          ]);
                          const sheet = extractFirstValue(item, [
                            'sheet',
                            'section',
                            'group',
                          ]);
                          const status = extractFirstValue(item, ['status']);
                          const description = extractFirstValue(item, [
                            'notes',
                            'description',
                            'explanation',
                            'message',
                            'details',
                          ]);
                          const doc1 = extractFirstValue(item, [
                            'doc1',
                            'document1',
                            'source_document',
                            'sourceDocument',
                          ]);
                          const doc2 = extractFirstValue(item, [
                            'doc2',
                            'document2',
                            'target_document',
                            'targetDocument',
                          ]);
                          const fieldDocument =
                            doc1 || doc2
                              ? `${formatCompactValue(
                                  doc1 || '—',
                                )} → ${formatCompactValue(doc2 || '—')}`
                              : extractFirstValue(item, [
                                  'field_document',
                                  'fieldDocument',
                                  'document',
                                  'source',
                                ]);
                          const expected = extractFirstValue(item, [
                            'value1',
                            'expected',
                            'expected_value',
                            'expectedValue',
                          ]);
                          const found = extractFirstValue(item, [
                            'value2',
                            'found',
                            'found_value',
                            'foundValue',
                            'actual',
                          ]);
                          const rowDescription = [
                            sheet,
                            description ||
                              (status ? humanizeKey(String(status)) : null),
                          ]
                            .filter(Boolean)
                            .join(' · ');

                          return (
                            <React.Fragment key={`${formatValue(title)}-${index}`}>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.5,
                                  borderBottom: '1px solid var(--es-soft-border)',
                                }}
                              >
                                <Chip
                                  size='small'
                                  variant='outlined'
                                  label={humanizeKey(severity)}
                                  sx={{
                                    color: severityColor,
                                    borderColor: severityColor,
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.5,
                                  borderBottom: '1px solid var(--es-soft-border)',
                                }}
                              >
                                <Typography sx={{ fontWeight: 800 }}>
                                  {formatCompactValue(
                                    title || `Discrepancy ${index + 1}`,
                                  )}
                                </Typography>
                                {rowDescription && rowDescription !== title && (
                                  <Typography
                                    variant='body2'
                                    sx={{ color: 'var(--text-secondary)', mt: 0.5 }}
                                  >
                                    {formatCompactValue(rowDescription)}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.5,
                                  borderBottom: '1px solid var(--es-soft-border)',
                                }}
                              >
                                <Typography variant='body2'>
                                  {formatCompactValue(fieldDocument)}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.5,
                                  borderBottom: '1px solid var(--es-soft-border)',
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{ color: 'var(--text-secondary)' }}
                                >
                                  {/* Pass the raw value: formatCompactValue
                                      already renders null/undefined/'' as '—',
                                      so a legitimate 0 or false is kept instead
                                      of being hidden by `|| '—'`. */}
                                  {formatCompactValue(expected)}
                                  {' → '}
                                  <Box
                                    component='span'
                                    sx={{
                                      color:
                                        found === undefined ||
                                        found === null ||
                                        found === ''
                                          ? 'var(--text-secondary)'
                                          : '#ef4444',
                                    }}
                                  >
                                    {formatCompactValue(found)}
                                  </Box>
                                </Typography>
                              </Box>
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <Box component='pre' sx={codeBlockSx}>
                      {formatValue(discrepancyDetails)}
                    </Box>
                  )}

                  {missingDocuments.length > 0 && (
                    <>
                      <Divider sx={{ borderColor: 'var(--border-color)' }} />
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Typography variant='h6'>Missing documents</Typography>
                        <Chip
                          variant='outlined'
                          label={`${missingDocuments.length} missing`}
                          sx={{
                            color: '#f59e0b',
                            borderColor: 'rgba(245, 158, 11, 0.45)',
                          }}
                        />
                      </Stack>
                      <Stack divider={<Divider sx={{ borderColor: 'var(--border-color)' }} />} spacing={1}>
                        {missingDocuments.map((doc, index) => (
                          <Stack
                            key={`${doc}-${index}`}
                            direction='row'
                            spacing={1.5}
                            alignItems='center'
                            sx={{ py: 0.5 }}
                          >
                            <Chip
                              size='small'
                              variant='outlined'
                              label='Missing'
                              sx={{
                                color: '#f59e0b',
                                borderColor: 'rgba(245, 158, 11, 0.45)',
                              }}
                            />
                            <Typography>{doc}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>

          <Stack direction='column' spacing={2.5} sx={{ minWidth: 0, width: '100%' }}>
            {/* Package checker never extracts/writes any files (see
                analyzer_service.package_check_document_batch, which never
                touches artifact_keys) -- only Entry parsing and Full comparison
                produce downloadable CSV/JSON/Excel output, so the download UI
                is pointless noise on a package-check run. */}
            {analysisMode !== 'package-check' && (
              <Paper
                sx={{
                  p: { xs: 2.25, md: 3 },
                  flex: 'initial',
                  minWidth: 0,
                  width: '100%',
                  ...helperCardSx,
                }}
              >
                <Stack spacing={2}>
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Box>
                      <Typography variant='h6'>Artifacts</Typography>
                      <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                        Downloadable outputs ready for handoff.
                      </Typography>
                    </Box>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      {isLoadingArtifacts && <CircularProgress size={16} />}
                      <Button
                        variant='text'
                        startIcon={<RefreshOutlinedIcon fontSize='small' />}
                        onClick={onRefreshArtifacts}
                        disabled={isLoadingArtifacts}
                      >
                        Refresh
                      </Button>
                      <Button
                        variant='outlined'
                        startIcon={<DownloadOutlinedIcon />}
                        disabled={!downloadableArtifacts.length}
                        data-testid='download-all-artifacts-button'
                        onClick={() => onDownloadAllArtifacts(downloadableArtifacts)}
                      >
                        Download all
                      </Button>
                    </Stack>
                  </Stack>
                  {downloadableArtifacts.length ? (
                    <Stack
                      divider={<Divider sx={{ borderColor: 'var(--border-color)' }} />}
                      spacing={1.25}
                      sx={{
                        maxHeight: 296,
                        overflowY: downloadableArtifacts.length > 3 ? 'auto' : 'visible',
                        pr: downloadableArtifacts.length > 3 ? 0.5 : 0,
                      }}
                    >
                      {downloadableArtifacts.map((artifact, index) => {
                        const label = resolveArtifactLabel(artifact, index);
                        const extension = label.includes('.')
                          ? label.split('.').pop()?.toUpperCase()
                          : 'FILE';
                        return (
                          <Stack
                            key={`${label}-${index}`}
                            data-testid='artifact-row'
                            data-artifact-label={label}
                            direction='row'
                            spacing={1.5}
                            alignItems='center'
                            justifyContent='space-between'
                            sx={{ py: 0.5 }}
                          >
                            <Stack direction='row' spacing={1.5} alignItems='center' sx={{ minWidth: 0 }}>
                              <Chip
                                size='small'
                                variant='outlined'
                                label={extension}
                                sx={{
                                  color: 'var(--action-color)',
                                  borderColor: 'rgba(3, 169, 244, 0.45)',
                                }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ wordBreak: 'break-word' }}>
                                  {label}
                                </Typography>
                                <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                                  {formatCompactValue(
                                    artifact?.description ||
                                      artifact?.type ||
                                      artifact?.mime_type ||
                                      artifact?.mimeType ||
                                      'Ready',
                                  )}
                                </Typography>
                              </Box>
                            </Stack>
                            <Button
                              variant='text'
                              startIcon={<DownloadOutlinedIcon />}
                              aria-label={`Download ${label}`}
                              onClick={() => onDownloadArtifact(artifact, index)}
                            >
                              Download
                            </Button>
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity='info'>No artifacts returned yet.</Alert>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Stack>
      </>
      )}

      {/* "What would you like to do next" and the ES-003 comparison stay
          visible regardless of isExpanded -- collapsing the breakdown
          shouldn't take away the actions the user came here for. */}
      <Box>
        <Typography variant='body2' sx={{ color: 'var(--text-secondary)', mb: 1 }}>
          What would you like to do next?
        </Typography>
        <ol className='ce-next-step-options'>
          {nextStepOptions.map((option) => (
            <li key={option.key}>
              <button
                type='button'
                className='option-button'
                onClick={() => handleNextStepOptionClick(option)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ol>
      </Box>

      {/* Package checker never extracts entry lines (see
            analyzer_service.package_check_document_batch, which never
            writes product_rows.json) -- there's nothing for an ACE ES-003
            export to be diffed against yet, so this mirrors the same
            analysisMode !== 'package-check' gate the Artifacts card uses.
            Also gated on showEs003Section -- it only mounts once the user
            picks "Compare vs ACE ES-003" from the next-step options above. */}
      {analysisMode !== 'package-check' && showEs003Section && (
        <Paper sx={{ p: { xs: 2.25, md: 3 }, ...helperCardSx }} data-testid='es003-diff-card'>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
              alignItems='flex-start'
              spacing={1.5}
            >
              <Box>
                <Typography variant='h6'>Compare vs ACE ES-003</Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'var(--text-secondary)' }}
                >
                  Upload the ES-003 export from ACE (.csv or .xlsx) to
                  check field-by-field agreement with this batch&apos;s
                  parsed entry lines.
                </Typography>
              </Box>
              {es003Diff && (
                <Chip
                  color={
                    es003Diff.matched_line_count > 0 ? 'success' : 'default'
                  }
                  label={`${es003Diff.matched_line_count} of ${es003Diff.our_line_count} lines matched`}
                  data-testid='es003-summary-chip'
                />
              )}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <input
                ref={es003FileInputRef}
                hidden
                type='file'
                accept='.csv,.xlsx,.xlsm'
                onChange={handleEs003FileChange}
                data-testid='es003-file-input'
              />
              <Button
                variant='outlined'
                onClick={() => es003FileInputRef.current?.click()}
                sx={{ borderRadius: '999px' }}
              >
                {es003File ? 'Change File' : 'Browse ACE Export'}
              </Button>
              <Button
                variant='contained'
                startIcon={isDiffingEs003 ? <CircularProgress size={16} color='inherit' /> : undefined}
                onClick={handleRunEs003DiffClick}
                disabled={!es003File || isDiffingEs003}
                sx={{ minHeight: 44, px: 2.25, borderRadius: '999px' }}
                data-testid='run-es003-diff-button'
              >
                {isDiffingEs003 ? 'Comparing…' : 'Run Comparison'}
              </Button>
            </Stack>

            {es003File && (
              <Box
                className='ce-file-chip'
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                data-testid='es003-selected-file-chip'
              >
                <Typography variant='body2' sx={{ color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
                  {es003File.name} · {formatFileSize(es003File.size)}
                </Typography>
                <IconButton
                  aria-label={`Remove ${es003File.name}`}
                  onClick={handleClearEs003File}
                  className='ce-file-chip-remove'
                  data-testid='es003-clear-file-button'
                  sx={{ width: 22, height: 22, fontSize: '15px' }}
                >
                  ×
                </IconButton>
              </Box>
            )}

            {es003Error && (
              <Alert severity='error' data-testid='es003-error-alert'>
                {es003Error}
              </Alert>
            )}

            {es003Diff && (
              <Stack spacing={2}>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`${es003Diff.entries_in_both?.length || 0} entries in both`}
                  />
                  {Boolean(es003Diff.entries_only_in_batch?.length) && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`${es003Diff.entries_only_in_batch.length} only in this batch`}
                      sx={{ color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.45)' }}
                    />
                  )}
                  {Boolean(es003Diff.entries_only_in_ace_export?.length) && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`${es003Diff.entries_only_in_ace_export.length} only in ACE export`}
                    />
                  )}
                  {Boolean(es003Diff.unmapped_ace_columns?.length) && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`${es003Diff.unmapped_ace_columns.length} unrecognized ACE columns`}
                    />
                  )}
                </Stack>

                <Stack
                  spacing={1.25}
                  sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}
                >
                  {es003FieldRows.map(({ field, stats, mapping }) => (
                    <Box
                      key={field}
                      sx={{
                        border: '1px solid var(--es-soft-border)',
                        borderRadius: 2,
                        p: 1.5,
                      }}
                      data-testid='es003-field-row'
                      data-field-name={field}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent='space-between'
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            flexWrap='wrap'
                            useFlexGap
                          >
                            <Typography sx={{ fontWeight: 700 }}>
                              {humanizeKey(field)}
                            </Typography>
                            {mapping.confidence === 'inferred' && (
                              <Chip
                                size='small'
                                variant='outlined'
                                label={`inferred (${Math.round((mapping.score || 0) * 100)}%)`}
                                sx={{ color: 'var(--action-color)', borderColor: 'rgba(3, 169, 244, 0.45)' }}
                              />
                            )}
                            {mapping.confidence === 'absent' && (
                              <Chip
                                size='small'
                                variant='outlined'
                                label='not in this ACE file'
                              />
                            )}
                          </Stack>
                          <Typography
                            variant='body2'
                            sx={{ color: 'var(--text-secondary)' }}
                          >
                            ACE column: {mapping.ace_column || '—'}
                          </Typography>
                        </Box>
                        <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap>
                          {stats.match > 0 && (
                            <Chip
                              size='small'
                              variant='outlined'
                              label={`${stats.match} match`}
                              sx={{ color: '#22c55e', borderColor: 'rgba(34, 197, 94, 0.45)' }}
                            />
                          )}
                          {stats.mismatch > 0 && (
                            <Chip
                              size='small'
                              variant='outlined'
                              label={`${stats.mismatch} mismatch`}
                              sx={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.45)' }}
                            />
                          )}
                          {stats.missing_in_ours > 0 && (
                            <Chip
                              size='small'
                              variant='outlined'
                              label={`${stats.missing_in_ours} missing (ours)`}
                              sx={{ color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.45)' }}
                            />
                          )}
                          {stats.missing_in_ace > 0 && (
                            <Chip
                              size='small'
                              variant='outlined'
                              label={`${stats.missing_in_ace} missing (ACE)`}
                              sx={{ color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.45)' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

interface CustomsEntryAnalyzerProps {
  // Renders without its own page header/title when hosted inline inside
  // another page's panel (e.g. a hub's Databases tab) instead of as the
  // standalone /customs-entry-analyzer route.
  embedded?: boolean;
  // Set when a database was just created specifically to receive this
  // upload (the "Upload Documents" Create Database path) -- locks the
  // "Send to Import Hub" step onto that database instead of asking the
  // user to pick or create one a second time after analysis. When absent,
  // the same values are read from router state (see CustomsEntryAnalyzerRouteState)
  // so a plain page navigation (not an embedded prop) can supply them too.
  presetDatabaseId?: string;
  presetDatabaseName?: string;
}

// Router state shape for reaching this page via history.push('/customs-entry-analyzer', state)
// instead of embedding it directly -- e.g. ImportDatabasePage's "Upload documents" choice.
export interface CustomsEntryAnalyzerRouteState {
  presetDatabaseId?: string;
  presetDatabaseName?: string;
  /** Breadcrumb back to wherever this navigation started from. */
  returnTo?: { label: string; path: string };
  /** A PDF picked before navigating here (e.g. Create Database's "Upload
   * documents" step) -- staged into the composer on mount so this page
   * lands ready to send instead of empty. Survives history.push because
   * it's an in-memory reference, not a serialized value. */
  presetFile?: File;
}

const CustomsEntryAnalyzer: React.FC<CustomsEntryAnalyzerProps> = ({
  embedded = false,
  presetDatabaseId: presetDatabaseIdProp,
  presetDatabaseName: presetDatabaseNameProp,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  const userId = useMemo(() => getCurrentUserId(cur_user), [cur_user]);
  const teamId = cur_user?.selected_team?.id || '';

  // "Send to Import Hub" -- lets the user push this batch's parsed line
  // items into an existing or brand-new Import Hub "products" database, via
  // a review screen (pick/name database -> review/edit/select rows -> save
  // -> see results). See mapEntryLinesToProducts above for the field
  // mapping. Duplicate review happens client-side, then saving uses Import
  // Hub's CSV endpoint so historical HTS codes reach screening instead of
  // being rejected by the single-product create endpoint.
  const [importHubDatabases, setImportHubDatabases] = useState<ImportServiceDatabase[]>([]);
  const [importHubModalOpen, setImportHubModalOpen] = useState(false);
  const [importHubTargetPayload, setImportHubTargetPayload] =
    useState<AnalysisResultPayload | null>(null);
  const [importHubStep, setImportHubStep] = useState<'select-database' | 'review' | 'results'>(
    'select-database',
  );
  const [importHubMode, setImportHubMode] = useState<'existing' | 'new'>('new');
  const [importHubSelectedId, setImportHubSelectedId] = useState('');
  const [importHubNewName, setImportHubNewName] = useState('');
  const [importHubDatabaseId, setImportHubDatabaseId] = useState('');
  const [isSendingToImportHub, setIsSendingToImportHub] = useState(false);
  const [importHubError, setImportHubError] = useState<string | null>(null);
  const [importHubReviewRows, setImportHubReviewRows] = useState<ImportHubReviewItem[]>([]);
  const [importHubSelectedRowIds, setImportHubSelectedRowIds] = useState<Set<string>>(new Set());
  // client_row_id -> warning from the last duplicate check -- kept separate
  // from the row data itself since it's a display concern, not something
  // the create call reads. No existingProductId/update action here --
  // Import Hub's backend has no update-product endpoint to target.
  const [importHubRowWarnings, setImportHubRowWarnings] = useState<Record<string, string>>({});
  const [importHubIsCheckingDuplicates, setImportHubIsCheckingDuplicates] = useState(false);
  const [importHubResult, setImportHubResult] = useState<ImportHubSendResult | null>(null);
  // client_row_id -> in-progress "find a current HTS replacement" state for
  // a failed row. Historical customs entries routinely carry an HTS code
  // that was valid when the 7501 was filed but has since expired/been
  // superseded -- createProduct only accepts codes effective as of TODAY
  // (see hts-classification.service.ts's searchCurrentCodes), so there's no
  // amount of re-parsing that fixes this. Keyed separately from
  // importHubResult since it's local UI state for the fix flow, not part of
  // the send outcome itself.
  const [importHubHtsFix, setImportHubHtsFix] = useState<
    Record<
      string,
      {
        status: 'idle' | 'loading' | 'loaded' | 'error';
        options: CurrentHtsOption[];
        selectedCode: string;
        manualCode: string;
        error?: string;
        retrying?: boolean;
      }
    >
  >({});

  useEffect(() => {
    if (!importHubModalOpen || !teamId) {
      setImportHubDatabases([]);
      return undefined;
    }
    // `active` guards against a stale response landing after teamId changes
    // (or the modal reopens for a different team) while this fetch is still
    // in flight -- without it, a slow request for the PREVIOUS team could
    // resolve after the switch and repopulate the list with the wrong
    // team's databases.
    let active = true;
    setImportHubDatabases([]);
    fetchImportDatabasesByGroupId(teamId)
      .then((res) => {
        if (active && res.success) setImportHubDatabases(res.databases);
      })
      .catch(() => {
        if (active) setImportHubError('Failed to load Import Hub databases.');
      });
    return () => {
      active = false;
    };
  }, [importHubModalOpen, teamId]);

  // Upload staging (single PDF only, same validation as before).
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [additionalColumns] = useState('');
  const [analysisMode, setAnalysisMode] = useState(ANALYSIS_MODES[0].value);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // The "current" batch -- the one Save / Saved-analyses / the next chat
  // question act on. Each completed run also freezes its own copy inside
  // that message's resultPayload, so older runs in the same conversation
  // keep rendering their own results even after a newer run replaces these.
  const [documentBatchId, setDocumentBatchId] = useState('');
  const [analysisResponse, setAnalysisResponse] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<CustomsUploadProgress | null>(null);

  const [isSaved, setIsSaved] = useState(false);
  const [savedTitle, setSavedTitle] = useState('');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  // Saving now also creates a Saved-Work ticket (see handleSaveAsTicket
  // below), the same CasesAPI.createCase call the Tariff tool uses -- these
  // two fields mirror CreateTicketWizard's own state shape (TariffPage.tsx).
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [savingTicket, setSavingTicket] = useState(false);

  // "Confirm before leaving" guard -- same pattern as TariffPage.tsx /
  // BeneficialOwnershipWorkflow.tsx (react-router v5's <Prompt> +
  // beforeunload). No shared hook exists for this in the codebase; each tool
  // keeps its own copy.
  const history = useHistory();
  const location = useLocation();
  const routeState = location.state as CustomsEntryAnalyzerRouteState | undefined;
  const presetDatabaseId = presetDatabaseIdProp ?? routeState?.presetDatabaseId;
  const presetDatabaseName = presetDatabaseNameProp ?? routeState?.presetDatabaseName;
  // A preset database id can come from a host that isn't actually Import Hub
  // (e.g. Export Hub's "Upload Documents" Create Database path currently
  // hands over an ExportDatabase id, since this tool only ever writes to
  // Import Hub -- the mode='export' work to fix that at the source hasn't
  // been built yet). Rather than lock the send-step onto an id that will
  // 404 there, confirm it's a real Import Hub database first; null while
  // checking (or when there's no preset to check) is treated as "don't lock"
  // so we never commit to something that turns out to be wrong.
  const [presetDatabaseValid, setPresetDatabaseValid] = useState<
    boolean | null
  >(null);
  useEffect(() => {
    if (!presetDatabaseId) {
      setPresetDatabaseValid(null);
      return undefined;
    }
    let cancelled = false;
    fetchImportDatabaseById(presetDatabaseId).then(({ success, database }) => {
      if (!cancelled) setPresetDatabaseValid(success && !!database);
    });
    return () => {
      cancelled = true;
    };
  }, [presetDatabaseId]);
  const useLockedPreset = !!presetDatabaseId && presetDatabaseValid === true;
  // Stage a file handed over via router state (Create Database's "Upload
  // documents" step) as if the user had just attached it, so this page
  // lands ready to send instead of empty. handleFiles is declared further
  // down but this only runs after the whole component body (including that
  // declaration) has executed, since effects fire post-render -- see
  // "one-shot on arrival" below for the same pattern.
  const presetFileHandledRef = useRef(false);
  useEffect(() => {
    const presetFile = routeState?.presetFile;
    if (!presetFile || presetFileHandledRef.current) return;
    presetFileHandledRef.current = true;
    handleFiles([presetFile]);
  }, [routeState?.presetFile]);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(
    () => localStorage.getItem(LEAVE_WARNING_STORAGE_KEY) === 'true',
  );
  const allowNavigationRef = useRef(false);
  const leaveSaveInProgressRef = useRef(false);

  // Chat state -- the single source of truth for the left column.
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isAgentResponding, setIsAgentResponding] = useState(false);
  const [hasAskedQuestion, setHasAskedQuestion] = useState(false);
  // "Ask about this analysis" (one of the "what would you like to do next"
  // options) reveals the starter-prompt row on demand instead of it always
  // appearing the moment a batch completes.
  const [showTryAsking, setShowTryAsking] = useState(false);
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const previousMessageCountRef = useRef(0);
  const wasAgentRespondingRef = useRef(false);
  const wasSubmittingRef = useRef(false);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Tracks the in-flight Q&A stream so Clear/New chat can cancel it --
  // otherwise a response still streaming in keeps appending assistant
  // messages after the chat was cleared, silently "un-clearing" it.
  const activeStreamRef = useRef<AbortController | null>(null);

  // Drives the "Thought for Ns" live counter on the thinking card below --
  // same timing pattern EARChatUI/BeneficialOwnershipWorkflow use.
  const [thinkingElapsedSeconds, setThinkingElapsedSeconds] = useState(0);
  const thinkingStartedAtRef = useRef<number | null>(null);

  // Live step narration for the thinking card -- one entry per tool_call
  // event the backend agent loop emits (run_agent_stream in agent.py),
  // marked done when its matching tool_result arrives. Reset at the start
  // of each question so a follow-up doesn't inherit the previous turn's steps.
  const [thinkingSteps, setThinkingSteps] = useState<
    { name: string; message: string; done: boolean }[]
  >([]);

  useEffect(() => {
    if (!isAgentResponding) {
      thinkingStartedAtRef.current = null;
      setThinkingElapsedSeconds(0);
      return () => undefined;
    }
    thinkingStartedAtRef.current = Date.now();
    setThinkingElapsedSeconds(0);
    const interval = setInterval(() => {
      if (thinkingStartedAtRef.current) {
        setThinkingElapsedSeconds(
          Math.floor((Date.now() - thinkingStartedAtRef.current) / 1000),
        );
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isAgentResponding]);

  // Same live counter, but for the upload/analysis-run status bubble
  // (isSubmitting) instead of the ask-the-agent one (isAgentResponding).
  const [submittingElapsedSeconds, setSubmittingElapsedSeconds] = useState(0);
  const submittingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isSubmitting) {
      submittingStartedAtRef.current = null;
      setSubmittingElapsedSeconds(0);
      return () => undefined;
    }
    submittingStartedAtRef.current = Date.now();
    setSubmittingElapsedSeconds(0);
    const interval = setInterval(() => {
      if (submittingStartedAtRef.current) {
        setSubmittingElapsedSeconds(
          Math.floor((Date.now() - submittingStartedAtRef.current) / 1000),
        );
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isSubmitting]);

  // Scroll to the bottom when a genuinely new message is appended, or when a
  // turn that was streaming text deltas (or upload/analysis status lines)
  // into an EXISTING message just finished — never while it's still going,
  // so an ongoing response never yanks the view back down while someone
  // reads an earlier message. Without those two completion edges, a long
  // streamed answer (or a result message whose artifacts get merged in after
  // it first appears) could finish well past the last scroll position with
  // nothing left to bring it back into view.
  useEffect(() => {
    const isNewMessage = messages.length !== previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    const justFinishedResponding = wasAgentRespondingRef.current && !isAgentResponding;
    wasAgentRespondingRef.current = isAgentResponding;

    const justFinishedSubmitting = wasSubmittingRef.current && !isSubmitting;
    wasSubmittingRef.current = isSubmitting;

    if (!isNewMessage && !justFinishedResponding && !justFinishedSubmitting) {
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAgentResponding, isSubmitting]);

  // Mirrors TariffPage.tsx's hasUnsavedTariffWork / BeneficialOwnershipWorkflow.tsx's
  // hasUnsavedWorkflowWork -- any real engagement (a staged file, a batch, or
  // chat history) that hasn't been saved yet. Also gated on the page's own
  // Save/Import-Hub modals not already being open, matching BO's `!isModalOpen`
  // guard, so this dialog never stacks on top of another one.
  const hasUnsavedCustomsWork =
    Boolean(stagedFile || documentBatchId || messages.length > 0) && !isSaved;
  const shouldWarnOnLeave =
    !disableLeaveWarning && hasUnsavedCustomsWork && !isSaveModalOpen && !importHubModalOpen;

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return undefined;
    }
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = () => {
    if (!pendingLocation) {
      return;
    }
    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  };

  const continueToPendingLocation = () => {
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  };

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }
    if (!shouldWarnOnLeave) {
      return true;
    }
    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    handleNewChat();
    continueToPendingLocation();
  };

  const handleSaveFromLeaveModal = () => {
    if (!documentBatchId) {
      return;
    }
    leaveSaveInProgressRef.current = true;
    setIsLeaveModalOpen(false);
    setIsSaveModalOpen(true);
  };

  const handleLeaveAndOptOut = () => {
    localStorage.setItem(LEAVE_WARNING_STORAGE_KEY, 'true');
    setDisableLeaveWarning(true);
    handleLeaveWithoutSaving();
  };

  // Auto-grows the composer textarea (34px -> 120px, same bounds as the
  // shared standard-workflow-composer-field CSS) since this composer uses
  // plain DOM elements instead of the shared WorkflowComposer component.
  useEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = '34px';
    const nextHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${Math.max(34, nextHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 121 ? 'auto' : 'hidden';
  }, [input]);

  const selectedMode = ANALYSIS_MODES.find((mode) => mode.value === analysisMode);
  const selectedModeLabel = selectedMode?.label || analysisMode;

  const pushMessage = (message: Omit<ChatMessage, 'id'> & { id?: string }) => {
    const withId: ChatMessage = { id: message.id || msgId(), ...message };
    setMessages((prev) => [...prev, withId]);
    return withId.id;
  };

  const pushResultMessage = (payload: AnalysisResultPayload): string => {
    const id = msgId();
    setMessages((prev) => [
      ...prev,
      { id: id, role: 'assistant', text: '', resultPayload: payload },
    ]);
    return id;
  };

  const updateResultMessageArtifacts = (id: string, artifacts: any[]) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id && m.resultPayload
          ? { ...m, resultPayload: { ...m.resultPayload, artifactList: artifacts } }
          : m,
      ),
    );
  };

  // Returns the merged artifact list instead of only setting page state, so
  // both a fresh run and an explicit "Refresh" on an older result message can
  // reuse it to update that specific message.
  const loadArtifacts = async (
    batchId: string,
    seedArtifacts: any[] = [],
  ): Promise<any[]> => {
    if (!batchId) {
      return seedArtifacts;
    }

    setIsLoadingArtifacts(true);
    try {
      const response = await CustomsAnalyzerAPI.listArtifacts(batchId, userId);
      const fromEndpoint = extractArtifacts(response);
      return extractArtifacts({ artifacts: [...seedArtifacts, ...fromEndpoint] });
    } catch (artifactError) {
      const message =
        artifactError instanceof Error
          ? artifactError.message
          : 'Failed to load artifacts.';
      enqueueSnackbar(message, { variant: 'warning' });
      return seedArtifacts;
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  const handleRefreshArtifacts = async (
    messageId: string,
    batchId: string,
    currentArtifacts: any[],
  ) => {
    const merged = await loadArtifacts(batchId, currentArtifacts);
    updateResultMessageArtifacts(messageId, merged);
  };

  const updateResultMessageEs003Diff = (id: string, diff: any) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id && m.resultPayload
          ? { ...m, resultPayload: { ...m.resultPayload, es003Diff: diff } }
          : m,
      ),
    );
  };

  const handleRunEs003Diff = async (
    messageId: string,
    batchId: string,
    file: File,
  ) => {
    try {
      const response = await CustomsAnalyzerAPI.diffEs003(batchId, file, userId);
      const diff = response?.diff || response;
      updateResultMessageEs003Diff(messageId, diff);
      enqueueSnackbar('ES-003 comparison complete.', { variant: 'success' });
      return diff;
    } catch (diffError) {
      const message =
        diffError instanceof Error
          ? diffError.message
          : 'Failed to compare the ACE ES-003 export.';
      enqueueSnackbar(message, { variant: 'error' });
      throw diffError;
    }
  };

  // "Send to X" is a preview for the other tools -- no destination accepts a
  // batch handoff yet, so those just acknowledge the choice in-chat (mirrors
  // EARChatUI's handleOptionClick placeholder replies). "Send to Import Hub"
  // is the one real integration.
  const handleSendToToolOption = (toolName: string, payload: AnalysisResultPayload) => {
    if (toolName === 'Import Hub') {
      setImportHubTargetPayload(payload);
      setImportHubStep('select-database');
      setImportHubMode(
        useLockedPreset
          ? 'existing'
          : importHubDatabases.length
          ? 'existing'
          : 'new',
      );
      setImportHubSelectedId(
        useLockedPreset
          ? presetDatabaseId!
          : importHubDatabases[0]?.id || '',
      );
      // Falls back to the preset's own name (e.g. an Export Hub database
      // this couldn't actually target) rather than losing that context,
      // then the generic batch label if there's no preset at all.
      setImportHubNewName(
        !useLockedPreset && presetDatabaseName
          ? presetDatabaseName
          : `Customs batch ${payload.documentBatchId}`,
      );
      setImportHubDatabaseId('');
      setImportHubReviewRows([]);
      setImportHubSelectedRowIds(new Set());
      setImportHubRowWarnings({});
      setImportHubResult(null);
      setImportHubError(null);
      setImportHubModalOpen(true);
      return;
    }
    pushMessage({ role: 'user', text: `Send to ${toolName}` });
    pushMessage({
      role: 'assistant',
      text: `${toolName} isn't connected yet — this is a preview of "Send to ${toolName}". Once that integration is built, this will hand this batch's parsed data straight into ${toolName}.`,
    });
  };

  const handleCloseImportHubModal = () => {
    if (isSendingToImportHub) return;
    setImportHubModalOpen(false);
  };

  // Step "select database" -> "review": resolve the target database
  // (creating it if needed), pull this batch's parsed line items, map them
  // to editable review rows (all selected by default), then run a
  // client-side duplicate check so warnings are visible before anything is
  // actually written -- Import Hub's backend has no dry-run/review endpoint
  // of its own to call instead.
  const handleImportHubNext = async () => {
    if (!importHubTargetPayload || !userId) {
      return;
    }
    if (!teamId) {
      setImportHubError('No active workspace/team selected.');
      return;
    }
    if (!cur_user?.basics?.id) {
      setImportHubError('Could not determine the current user -- please refresh and try again.');
      return;
    }
    if (importHubMode === 'existing' && !importHubSelectedId) {
      setImportHubError('Select a database, or switch to "Create new".');
      return;
    }
    if (importHubMode === 'new' && !importHubNewName.trim()) {
      setImportHubError('Name the new database.');
      return;
    }

    setIsSendingToImportHub(true);
    setImportHubError(null);
    try {
      let databaseId = importHubSelectedId;
      if (importHubMode === 'new') {
        const dbResult = await createImportDatabase({
          name: importHubNewName.trim(),
          groupId: teamId,
          createdBy: cur_user.basics.id,
          description: `Created from Customs Entry Analyzer batch ${importHubTargetPayload.documentBatchId}`,
          // Unset by default (see CreateImportDatabaseInput) -- without this,
          // getDatabaseNotificationChannels always resolves sendInApp: false
          // for this database, so a completed screening never notifies.
          notificationChannels: ['In-app Notification'],
        });
        if (!dbResult.success || !dbResult.database?.id) {
          throw new Error(dbResult.error || 'Failed to create the database.');
        }
        databaseId = dbResult.database.id;
      }

      const blob = await CustomsAnalyzerAPI.downloadArtifact(
        importHubTargetPayload.documentBatchId,
        'entry_parser/product_rows.json',
        userId,
      );
      const lines = JSON.parse(await blob.text());
      if (!Array.isArray(lines) || !lines.length) {
        throw new Error(
          'No parsed line items found for this batch -- run Entry parsing or Full comparison first.',
        );
      }

      const rows = mapEntryLinesToProducts(lines);
      setImportHubDatabaseId(databaseId);
      setImportHubReviewRows(rows);
      setImportHubSelectedRowIds(new Set(rows.map((row) => row._client_row_id)));
      setImportHubStep('review');
      const duplicateCheckCompleted = await runImportHubDuplicateCheck(databaseId, rows);
      if (!duplicateCheckCompleted) {
        throw new Error(
          'Could not verify the destination database. No products can be sent until the duplicate check succeeds.',
        );
      }
    } catch (sendError) {
      const message =
        sendError instanceof Error ? sendError.message : 'Failed to prepare the Import Hub import.';
      setImportHubError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSendingToImportHub(false);
    }
  };

  // A customs entry number + line number is the primary record identity; a
  // normalized full-row fingerprint is the fallback. Duplicates are marked
  // skip automatically, both for the destination and within this batch.
  const runImportHubDuplicateCheck = async (
    databaseId: string,
    rows: ImportHubReviewItem[],
  ): Promise<boolean> => {
    if (!databaseId || !rows.length) return false;
    setImportHubIsCheckingDuplicates(true);
    try {
      const products = await fetchAllImportProducts(databaseId);
      const existingKeys = new Set(products.map(getImportHubDuplicateKey));
      const batchKeys = new Set<string>();
      const warningsByRowId: Record<string, string> = {};
      rows.forEach((row) => {
        const duplicateKey = getImportHubDuplicateKey(row);
        if (existingKeys.has(duplicateKey)) {
          warningsByRowId[row._client_row_id] =
            'This customs record already exists in this database and will be skipped.';
        } else if (batchKeys.has(duplicateKey)) {
          warningsByRowId[row._client_row_id] =
            'This customs record is repeated in this batch and will be skipped.';
        }
        batchKeys.add(duplicateKey);
      });
      setImportHubReviewRows((currentRows) =>
        currentRows.map((row) => ({
          ...row,
          _action: warningsByRowId[row._client_row_id] ? 'skip' : 'create',
        })),
      );
      setImportHubRowWarnings(warningsByRowId);
      setImportHubError(null);
      return true;
    } catch (checkError) {
      const message =
        checkError instanceof Error
          ? checkError.message
          : 'Failed to check the Import Hub database for duplicates.';
      setImportHubError(
        `${message} No products will be sent until the duplicate check succeeds.`,
      );
      return false;
    } finally {
      setImportHubIsCheckingDuplicates(false);
    }
  };

  const handleToggleImportHubRowSelected = (clientRowId: string) => {
    setImportHubSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientRowId)) {
        next.delete(clientRowId);
      } else {
        next.add(clientRowId);
      }
      return next;
    });
  };

  const handleToggleAllImportHubRowsSelected = () => {
    setImportHubSelectedRowIds((prev) =>
      prev.size === importHubReviewRows.length
        ? new Set()
        : new Set(importHubReviewRows.map((row) => row._client_row_id)),
    );
  };

  // Editable columns only -- a plain switch on literal field names (not a
  // computed `[field]:` assignment) so each branch's value type stays
  // exact and unambiguous.
  type ImportHubEditableField =
    | 'name'
    | 'hts_code'
    | 'country_of_origin'
    | 'manufacturer'
    | 'product_value'
    | 'quantity';

  const handleUpdateImportHubRowField = (
    clientRowId: string,
    field: ImportHubEditableField,
    value: string,
  ) => {
    setImportHubReviewRows((prev) =>
      prev.map((row) => {
        if (row._client_row_id !== clientRowId) return row;
        switch (field) {
          case 'name':
            return { ...row, name: value };
          case 'hts_code':
            return { ...row, hts_code: value };
          case 'country_of_origin':
            return { ...row, country_of_origin: value };
          case 'manufacturer':
            return { ...row, manufacturer: value };
          case 'product_value':
            return { ...row, product_value: value === '' ? undefined : Number(value) };
          case 'quantity':
            return { ...row, quantity: value === '' ? undefined : Number(value) };
          default:
            return row;
        }
      }),
    );
  };

  // Step "review" -> "results": upload the selected rows through the same
  // permissive CSV path used by Import Hub's own Import Data action. Product
  // creation must not reject a historical HTS code before screening can
  // classify it. Existing database matches are skipped, while repeated lines
  // inside this batch get a customs entry/line suffix so none are discarded.
  const handleConfirmSendToImportHub = async () => {
    if (!importHubTargetPayload || !importHubDatabaseId) {
      return;
    }
    const rowsToSend = importHubReviewRows.filter((row) =>
      importHubSelectedRowIds.has(row._client_row_id),
    );
    if (!rowsToSend.length) {
      setImportHubError('Select at least one product to send.');
      return;
    }

    setIsSendingToImportHub(true);
    setImportHubError(null);
    try {
      const result: ImportHubSendResult = { created: [], skipped: [], failed: [] };

      const requestedRows: ImportHubReviewItem[] = [];

      rowsToSend.forEach((row) => {
        if (row._action === 'skip') {
          result.skipped.push({
            client_row_id: row._client_row_id,
            name: row.name,
            reason:
              importHubRowWarnings[row._client_row_id] ||
              'This record was marked to be skipped.',
          });
        } else {
          requestedRows.push(row);
        }
      });

      let attemptedRows = requestedRows;
      try {
        const existingProducts = await fetchAllImportProducts(importHubDatabaseId);
        const existingDuplicateKeys = new Set(
          existingProducts.map(getImportHubDuplicateKey),
        );
        const batchDuplicateKeys = new Set<string>();
        const existingProductKeys = new Set(
          existingProducts.map(
            (product) =>
              `${(product.name || '').trim()}::${(
                product.country_of_origin || ''
              ).trim()}`,
          ),
        );
        const batchProductKeys = new Set<string>();
        const rowsToImport: ImportHubReviewItem[] = [];

        requestedRows.forEach((row) => {
          const duplicateKey = getImportHubDuplicateKey(row);
          if (
            existingDuplicateKeys.has(duplicateKey) ||
            batchDuplicateKeys.has(duplicateKey)
          ) {
            result.skipped.push({
              client_row_id: row._client_row_id,
              name: row.name,
              reason: existingDuplicateKeys.has(duplicateKey)
                ? 'This customs record already exists in this database.'
                : 'This customs record is repeated in this batch.',
            });
            return;
          }
          batchDuplicateKeys.add(duplicateKey);

          const key = `${(row.name || '').trim()}::${(
            row.country_of_origin || ''
          ).trim()}`;

          let rowToImport = row;
          let importKey = key;
          let collisionNumber = 1;
          while (
            batchProductKeys.has(importKey) ||
            existingProductKeys.has(importKey)
          ) {
            rowToImport = addCustomsLineIdentity(row, collisionNumber);
            importKey = `${rowToImport.name.trim()}::${(
              rowToImport.country_of_origin || ''
            ).trim()}`;
            collisionNumber += 1;
          }

          batchProductKeys.add(importKey);
          rowsToImport.push(rowToImport);
        });
        attemptedRows = rowsToImport;

        if (rowsToImport.length) {
          const { csvText, resolvedHeaders, notMappedColumns } =
            buildImportHubCsvUpload(rowsToImport);
          const file = new File(
            [csvText],
            `customs-entry-${importHubTargetPayload.documentBatchId}.csv`,
            { type: 'text/csv' },
          );
          const formData = new FormData();
          formData.append('file', file, file.name);
          formData.append('fileType', 'csv');
          formData.append('streamingImport', 'true');
          formData.append('database_id', importHubDatabaseId);
          formData.append('import_source', 'document_upload');
          formData.append('resolvedHeaders', JSON.stringify(resolvedHeaders));
          formData.append('notMappedColumns', JSON.stringify(notMappedColumns));

          const uploadResult = await bulkUploadImportProducts(formData);
          if (!uploadResult.success) {
            throw new Error(uploadResult.error || 'Failed to import products.');
          }

          if (uploadResult.status === 'queued') {
            if (!uploadResult.jobId) {
              throw new Error('Import Hub did not return an import job.');
            }

            const completedJob = await waitForImportProductJob(uploadResult.jobId);
            if (!completedJob || completedJob.status !== 'completed') {
              throw new Error(
                completedJob?.error || 'Import Hub did not complete the product import.',
              );
            }

            const errorsByRowIndex = new Map(
              completedJob.errors.map((error) => [error.row - 2, error.message]),
            );
            const rowsWithoutValidationErrors: ImportHubReviewItem[] = [];

            rowsToImport.forEach((row, index) => {
              const rowError = errorsByRowIndex.get(index);
              if (rowError) {
                result.failed.push({
                  client_row_id: row._client_row_id,
                  name: row.name,
                  reason: rowError,
                });
              } else {
                rowsWithoutValidationErrors.push(row);
              }
            });

            rowsWithoutValidationErrors.forEach((row, index) => {
              if (index < completedJob.insertedRows) {
                result.created.push({
                  client_row_id: row._client_row_id,
                  name: row.name,
                });
              } else {
                result.skipped.push({
                  client_row_id: row._client_row_id,
                  name: row.name,
                  reason: 'The bulk import identified this product as a duplicate.',
                });
              }
            });
          } else {
            rowsToImport.forEach((row) => {
              result.created.push({
                client_row_id: row._client_row_id,
                name: row.name,
              });
            });
          }
        }
      } catch (uploadError) {
        const reason =
          uploadError instanceof Error
            ? uploadError.message
            : 'Failed to import products.';
        attemptedRows.forEach((row) => {
          result.failed.push({
            client_row_id: row._client_row_id,
            name: row.name,
            reason: reason,
          });
        });
      }

      // Import Hub's backend auto-triggers a screening run as soon as
      // products are inserted (see server's createBulkProducts controller),
      // with no started-notification of its own -- fire one here so the
      // user isn't left guessing between "sent" and the eventual
      // "Screening Completed" notification (gated the same way, via
      // notificationChannels on the target database -- see
      // handleImportHubNext above).
      if (result.created.length && cur_user?.basics?.id && teamId) {
        const importHubDatabaseName =
          (importHubMode === 'new'
            ? importHubNewName.trim()
            : importHubDatabases.find((db) => db.id === importHubDatabaseId)?.name) ||
          'Import Hub database';
        NotificationAPI.createNotification({
          newNotification: {
            userId: cur_user.basics.id,
            groupId: teamId,
            status: 'unread',
            heading: `Screening Started for ${importHubDatabaseName}`,
            message: `Screening started for ${result.created.length} product${
              result.created.length === 1 ? '' : 's'
            } sent from Customs Entry Analyzer batch ${importHubTargetPayload.documentBatchId}.`,
            link: `/database/${importHubDatabaseId}`,
            type: 'bitedb',
          },
        }).catch(() => {
          // Best-effort -- a failed "started" ping shouldn't block the
          // Import Hub send flow that already succeeded.
        });
      }

      setImportHubResult(result);
      setImportHubStep('results');

      pushMessage({ role: 'user', text: 'Send to Import Hub' });
      pushMessage({
        role: 'assistant',
        text: `Sent to Import Hub: ${result.created.length} created, ${result.skipped.length} skipped, ${result.failed.length} failed.`,
      });
      enqueueSnackbar(
        `Import Hub: ${result.created.length} created, ${result.skipped.length} skipped, ${result.failed.length} failed.`,
        { variant: result.failed.length ? 'warning' : 'success' },
      );
    } catch (sendError) {
      const message =
        sendError instanceof Error ? sendError.message : 'Failed to send to Import Hub.';
      setImportHubError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSendingToImportHub(false);
    }
  };

  // Looks up still-current siblings of a rejected HTS code's 8-digit family
  // via product-service's own `search/hts-codes` (the same lookup its HTS
  // picker UI uses) -- no product-service changes needed, since that route
  // already runs unscoped from any specific product. Triggered lazily (on
  // click, not automatically for every failed row) since it's a network
  // call per row.
  const handleFindHtsReplacements = async (clientRowId: string) => {
    const row = importHubReviewRows.find((r) => r._client_row_id === clientRowId);
    if (!row) return;

    setImportHubHtsFix((prev) => ({
      ...prev,
      [clientRowId]: {
        status: 'loading',
        options: [],
        selectedCode: '',
        manualCode: '',
      },
    }));

    const familyPrefix = (row.hts_code || '').replace(/\D/g, '').slice(0, 8);
    const { success, options, error } = await findCurrentHtsCodes(
      familyPrefix,
      row.country_of_origin,
    );

    setImportHubHtsFix((prev) => ({
      ...prev,
      [clientRowId]: {
        status: success ? 'loaded' : 'error',
        options: options,
        selectedCode: '',
        manualCode: '',
        error: success ? undefined : error || 'Failed to look up current HTS codes.',
      },
    }));
  };

  // Re-runs createImportProduct for exactly one previously-failed row with a
  // corrected hts_code -- not a full resend, since the other rows in the
  // batch already succeeded or were deliberately skipped. On success, moves
  // the row from importHubResult.failed to .created so the results view
  // reflects the fix immediately.
  const handleRetryRowWithHts = async (clientRowId: string, newHtsCode: string) => {
    const row = importHubReviewRows.find((r) => r._client_row_id === clientRowId);
    const normalized = newHtsCode.trim();
    if (!row || !normalized || !importHubDatabaseId) return;

    setImportHubHtsFix((prev) => ({
      ...prev,
      [clientRowId]: { ...prev[clientRowId], retrying: true, error: undefined },
    }));

    try {
      const createResult = await createImportProduct(importHubDatabaseId, {
        name: row.name,
        description: row.description,
        category: row.category,
        hierarchy_type: row.hierarchy_type,
        hts_code: normalized,
        country_of_origin: row.country_of_origin,
        manufacturer: row.manufacturer,
        model_number: row.model_number,
        product_value: row.product_value,
        quantity: row.quantity,
        additional_columns: row.additional_columns,
        import_source: 'document_upload',
      });
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create product.');
      }

      setImportHubResult((prev) =>
        prev
          ? {
              ...prev,
              failed: prev.failed.filter((item) => item.client_row_id !== clientRowId),
              created: [
                ...prev.created,
                { client_row_id: clientRowId, id: createResult.productId, name: row.name },
              ],
            }
          : prev,
      );
      setImportHubHtsFix((prev) => {
        const next = { ...prev };
        delete next[clientRowId];
        return next;
      });
      enqueueSnackbar(`${row.name || 'Product'} created with HTS ${normalized}.`, {
        variant: 'success',
      });
    } catch (retryError) {
      setImportHubHtsFix((prev) => ({
        ...prev,
        [clientRowId]: {
          ...prev[clientRowId],
          retrying: false,
          error:
            retryError instanceof Error ? retryError.message : 'Failed to create product.',
        },
      }));
    }
  };

  // "Ask about this analysis" -- the last "what would you like to do next"
  // option -- reveals the starter-prompt row on demand instead of it always
  // appearing the moment a batch completes.
  const handleRevealTryAsking = () => {
    setShowTryAsking(true);
  };

  const handleArtifactDownload = async (
    batchId: string,
    artifact: any,
    index: number,
  ) => {
    if (!batchId) {
      return;
    }

    const artifactPath = extractArtifactPath(artifact);

    if (!artifactPath) {
      const directUrl =
        artifact?.url || artifact?.download_url || artifact?.downloadUrl;
      // Artifact metadata is backend-supplied, so only navigate to real
      // http(s) URLs — a malformed or hostile scheme (javascript:, data:)
      // must never reach window.open. Anything else falls through to the
      // "no downloadable path" warning below.
      if (typeof directUrl === 'string' && directUrl) {
        let parsedUrl: URL | null = null;
        try {
          parsedUrl = new URL(directUrl, window.location.origin);
        } catch {
          parsedUrl = null;
        }
        if (
          parsedUrl &&
          (parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:')
        ) {
          window.open(parsedUrl.toString(), '_blank', 'noopener,noreferrer');
          return;
        }
      }

      enqueueSnackbar(`Artifact ${index + 1} does not have a downloadable path.`, {
        variant: 'warning',
      });
      return;
    }

    try {
      const blob = await CustomsAnalyzerAPI.downloadArtifact(batchId, artifactPath, userId);
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = resolveArtifactLabel(artifact, index);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      const message =
        downloadError instanceof Error
          ? downloadError.message
          : 'Failed to download the artifact.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Saving now does two things: persists the batch in pyapi (as before, so
  // "Saved analyses" / leave-without-saving still work) AND creates a
  // Saved-Work ticket via CasesAPI, the same call the Tariff tool makes from
  // TariffPage.tsx's own createTicket -- so the analysis shows up in Saved
  // Work (Monitor.tsx) alongside every other tool's saved tickets. There is
  // no ticket-detail renderer for this docType yet (Monitor/ResultsTab only
  // branches on existing docTypes like 'Tariff'/'Classification'/'ECCN') --
  // that is separate, explicitly out-of-scope follow-up work.
  const handleSaveAsTicket = async (name: string) => {
    if (!documentBatchId) {
      return;
    }
    setSavingTicket(true);
    try {
      await CustomsAnalyzerAPI.saveBatch(
        documentBatchId,
        userId,
        true,
        name.trim() || undefined,
      );

      const ownerQuery = {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      };
      const resp = await CasesAPI.createCase({
        newCase: {
          ticketName: name,
          // MonitorCard's formatDate (utils/dateFormater.ts) splits this on
          // '-' expecting exactly [year, month, day] -- a full ISO timestamp
          // breaks it (the time portion isn't a valid "day"), which crashes
          // the whole Saved Work list, not just this ticket's card.
          lastUpdate: new Date().toISOString().slice(0, 10),
          docType: 'Customs Entry Analysis',
          sharedWith: shareEmail,
          status: 'Complete',
          favorite: false,
          groupName: cur_user.selected_team?.name,
          groupId: teamId,
          activity: {
            user: ownerQuery,
            action: 'saved work',
            timeStamp: Date.now(),
          },
          owner: ownerQuery,
          // The Cases schema (server/server/api/models/Case/Case.ts) is
          // fully strict/typed -- there is no free-form field like a
          // "link", so a made-up field name silently gets dropped on save.
          // workflowData: any[] is the field other docTypes (Export
          // Unified, EAR, Beneficial Ownership) already use the same way:
          // a docType-specific bucket, read via workflowData[0] after
          // checking docType first (see Ticket.tsx's redirect below).
          workflowData: [
            {
              documentBatchId: documentBatchId,
              mode: analysisMode,
            },
          ],
        },
      });
      if (!resp?.success) {
        throw new Error('Failed to create the Saved Work ticket.');
      }

      shareEmail.forEach((email) => {
        handleShareTicketNotification(name, cur_user, email, resp.result._id);
      });

      setIsSaved(true);
      setIsSaveModalOpen(false);
      enqueueSnackbar('Analysis saved to Saved Work.', { variant: 'success' });
      // Save was started from the "leave without saving?" modal -- now that
      // it succeeded, finish the navigation the user originally asked for.
      if (leaveSaveInProgressRef.current) {
        leaveSaveInProgressRef.current = false;
        continueToPendingLocation();
      }
    } catch (saveError) {
      const message =
        saveError instanceof Error ? saveError.message : 'Failed to save the analysis.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSavingTicket(false);
    }
  };

  // Saved analyses no longer has its own in-tool menu (see removed
  // "Saved analyses" button/Menu) -- every save now creates a Saved Work
  // ticket instead (handleSaveAsTicket above), and opening that ticket
  // redirects back here with ?batch=<id> (see Ticket.tsx), which resumes
  // the exact same way this function already did for the old menu.
  const resumeSavedBatch = async (batchId: string) => {
    try {
      const record = await CustomsAnalyzerAPI.getDocumentBatch(batchId, userId);
      const resumedAnalysisResponse = record.last_result || record;
      const initialArtifacts = extractArtifacts(resumedAnalysisResponse);

      setDocumentBatchId(batchId);
      setAnalysisResponse(resumedAnalysisResponse);
      setIsSaved(true);
      setSavedTitle(record.title || '');

      const priorMessages: ChatMessage[] = Array.isArray(record.conversation)
        ? record.conversation
            .filter(
              (turn: any) =>
                turn && (turn.role === 'user' || turn.role === 'assistant') && turn.text,
            )
            .map((turn: any) => ({
              id: msgId(),
              role: turn.role,
              text: turn.text,
              downloadLinks: Array.isArray(turn.download_links)
                ? turn.download_links
                : undefined,
            }))
        : [];

      const resultMessageId = msgId();
      setMessages([
        {
          id: msgId(),
          role: 'user',
          text: `Resumed batch ${batchId}${record.title ? ` — ${record.title}` : ''}.`,
        },
        {
          id: resultMessageId,
          role: 'assistant',
          text: '',
          resultPayload: {
            analysisResponse: resumedAnalysisResponse,
            analysisMode: analysisMode,
            documentBatchId: batchId,
            artifactList: initialArtifacts,
            es003Diff: record.es003_diff || null,
          },
        },
        ...priorMessages,
      ]);
      setHasAskedQuestion(priorMessages.length > 0);
      setShowTryAsking(false);

      const merged = await loadArtifacts(batchId, initialArtifacts);
      updateResultMessageArtifacts(resultMessageId, merged);
      syncBatchToLibrary(batchId);
    } catch (resumeError) {
      const message =
        resumeError instanceof Error
          ? resumeError.message
          : 'Failed to load the saved analysis.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Arriving here via a Saved Work ticket (Ticket.tsx redirects a
  // 'Customs Entry Analysis' case to /customs-entry-analyzer?batch=<id>)
  // resumes the exact batch that ticket points at, the same way the old
  // in-tool "Saved analyses" menu did.
  useEffect(() => {
    if (!userId) return;
    const batchId = new URLSearchParams(location.search).get('batch');
    if (batchId) {
      resumeSavedBatch(batchId);
    }
    // One-shot on arrival -- resumeSavedBatch is intentionally left out of
    // deps so this doesn't re-fire as the resumed state it sets updates.
  }, [userId, location.search]);

  const handleFiles = (files: File[]) => {
    if (!files.length) {
      return;
    }

    if (files.length > 1) {
      pushMessage({
        role: 'error',
        text: 'Upload one PDF only. Combine the entire documents into a single PDF before uploading.',
      });
      return;
    }

    const file = files[0];
    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      pushMessage({
        role: 'error',
        text: 'Only PDF files are supported for Document upload and analysis.',
      });
      return;
    }

    setStagedFile(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleChatDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleRemoveStagedFile = () => {
    setStagedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // runPackageCheck/runEntryParsing/runFullComparison kick off a background
  // job on pyapi and return immediately; poll the batch record for the same
  // result shape they used to return directly once the job finishes.
  const runSelectedMode = async (batchId: string, onStage?: (stage: string) => void) => {
    if (analysisMode === 'package-check') {
      await CustomsAnalyzerAPI.runPackageCheck(batchId, userId);
    } else if (analysisMode === 'ingest') {
      await CustomsAnalyzerAPI.runEntryParsing(batchId, userId);
    } else {
      await CustomsAnalyzerAPI.runFullComparison(batchId, userId);
    }
    return CustomsAnalyzerAPI.pollDocumentBatch(batchId, userId, undefined, undefined, onStage);
  };

  const syncBatchToLibrary = async (batchId: string) => {
    if (!batchId || !teamId) return;
    try {
      await DocumentLibraryAPI.syncDocumentBatch(batchId, teamId);
    } catch (error) {
      // Indexing is secondary to the analysis itself. We retry after the run
      // completes, so a transient Library failure must not fail the upload.
      console.warn('Unable to synchronize customs batch with Document Library.', error);
    }
  };

  const runAnalysisFromComposer = async (file: File, note: string) => {
    if (!userId) {
      pushMessage({
        role: 'error',
        text: 'No active user was found. Please refresh the page and try again.',
      });
      return;
    }

    pushMessage({
      role: 'user',
      text: note ? `Uploaded ${file.name} — ${note}` : `Uploaded ${file.name}`,
    });

    setIsSubmitting(true);
    setIsSaved(false);
    setSavedTitle('');

    const statusId = msgId();
    // `replaceLast` keeps rapid-fire progress ticks (many per upload) updating
    // the same line instead of flooding the Thinking card with one line per
    // percent; every other call appends a new, permanently visible step so
    // the whole run's history stays on screen instead of only the latest line.
    const upsertStatus = (text: string, replaceLast = false) => {
      setMessages((prev) => {
        const existing = prev.find((m) => m.id === statusId);
        if (!existing) {
          return [...prev, { id: statusId, role: 'status', text: text, steps: [text] }];
        }
        const prevSteps = existing.steps && existing.steps.length ? existing.steps : [existing.text];
        const nextSteps = replaceLast ? [...prevSteps.slice(0, -1), text] : [...prevSteps, text];
        return prev.map((m) => (m.id === statusId ? { ...m, text: text, steps: nextSteps } : m));
      });
    };

    upsertStatus('Uploading PDF…');
    let finishingUploadPushed = false;
    let activeBatchId = '';

    try {
      const batchResponse = await CustomsAnalyzerAPI.createDocumentBatch(
        file,
        userId,
        additionalColumns,
        (progress) => {
          setUploadProgress(progress);
          if (progress.percent >= 100) {
            // All bytes are sent at this point, but the server still has to
            // finish writing the file to S3/GridFS and respond -- that can
            // take several more seconds (e.g. 10s+ for a large PDF) with no
            // further upload-progress events to report. Push this as its
            // own new line (not a replaceLast update) exactly once -- the
            // browser can fire the progress callback more than once at 100%
            // -- so the Thinking card visibly moves instead of sitting on
            // "100%" for that whole gap and looking stuck.
            if (!finishingUploadPushed) {
              finishingUploadPushed = true;
              upsertStatus('Finishing upload…');
            }
          } else {
            upsertStatus(`Uploading PDF… ${progress.percent}%`, true);
          }
        },
      );

      const batchId = getBatchIdFromResponse(batchResponse);
      if (!batchId) {
        throw new Error('The analyzer did not return a document batch id.');
      }
      activeBatchId = batchId;

      setDocumentBatchId(batchId);
      syncBatchToLibrary(batchId);
      upsertStatus(`Upload complete. Running ${selectedModeLabel}…`);

      const analysisResult = await runSelectedMode(batchId, (stage) => upsertStatus(stage));
      setAnalysisResponse(analysisResult);
      upsertStatus('Analysis complete. Loading downloads…');

      const initialArtifacts = extractArtifacts(analysisResult);
      const resultMessageId = pushResultMessage({
        analysisResponse: analysisResult,
        analysisMode: analysisMode,
        documentBatchId: batchId,
        artifactList: initialArtifacts,
        es003Diff: null,
      });
      setHasAskedQuestion(false);
      setShowTryAsking(false);

      const mergedArtifacts = await loadArtifacts(batchId, initialArtifacts);
      updateResultMessageArtifacts(resultMessageId, mergedArtifacts);
      await syncBatchToLibrary(batchId);

      setMessages((prev) => prev.filter((m) => m.id !== statusId));
      enqueueSnackbar('Customs analysis completed.', { variant: 'success' });
    } catch (createError) {
      if (activeBatchId) {
        syncBatchToLibrary(activeBatchId);
      }
      const message =
        createError instanceof Error
          ? createError.message
          : 'Failed to submit customs analysis.';
      setMessages((prev) =>
        prev.map((m) => (m.id === statusId ? { ...m, role: 'error', text: message } : m)),
      );
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Lets the user try a different analysis mode against the batch they
  // already have -- e.g. run Full comparison after Entry parsing -- without
  // re-attaching the PDF. The mode endpoints (.../package-check, .../ingest,
  // .../analyze) already operate on an existing document_batch_id, so this
  // just calls runSelectedMode directly and skips createDocumentBatch.
  const runModeOnCurrentBatch = async () => {
    if (!documentBatchId || !userId) {
      return;
    }

    setIsSubmitting(true);
    setIsSaved(false);
    setSavedTitle('');

    const statusId = msgId();
    const initialStatusText = `Running ${selectedModeLabel} on batch ${documentBatchId}…`;
    setMessages((prev) => [
      ...prev,
      { id: statusId, role: 'status', text: initialStatusText, steps: [initialStatusText] },
    ]);
    const upsertStatus = (text: string) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === statusId ? { ...m, text: text, steps: [...(m.steps || [m.text]), text] } : m,
        ),
      );
    };

    try {
      const analysisResult = await runSelectedMode(documentBatchId, (stage) => upsertStatus(stage));
      setAnalysisResponse(analysisResult);

      const initialArtifacts = extractArtifacts(analysisResult);
      const resultMessageId = pushResultMessage({
        analysisResponse: analysisResult,
        analysisMode: analysisMode,
        documentBatchId: documentBatchId,
        artifactList: initialArtifacts,
        es003Diff: null,
      });

      const mergedArtifacts = await loadArtifacts(documentBatchId, initialArtifacts);
      updateResultMessageArtifacts(resultMessageId, mergedArtifacts);
      await syncBatchToLibrary(documentBatchId);

      setMessages((prev) => prev.filter((m) => m.id !== statusId));
      enqueueSnackbar(`${selectedModeLabel} completed on the existing batch.`, { variant: 'success' });
    } catch (rerunError) {
      const message =
        rerunError instanceof Error
          ? rerunError.message
          : `Failed to run ${selectedModeLabel} on this batch.`;
      setMessages((prev) =>
        prev.map((m) => (m.id === statusId ? { ...m, role: 'error', text: message } : m)),
      );
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadLink = async (link: ChatDownloadLink) => {
    setDownloadingPath(link.path);
    try {
      const blob = await CustomsAnalyzerAPI.downloadArtifact(documentBatchId, link.path, userId);
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = link.path.split('/').pop() || 'download';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to download the updated file.';
      pushMessage({ role: 'error', text: message });
    } finally {
      setDownloadingPath(null);
    }
  };

  const sendQuestion = async (text: string) => {
    const question = text.trim();
    if (!question || isAgentResponding) {
      return;
    }

    if (!documentBatchId || !analysisResponse) {
      pushMessage({ role: 'user', text: question });
      pushMessage({
        role: 'error',
        text: 'Attach a PDF and let it finish analyzing before asking questions about it.',
      });
      return;
    }

    setHasAskedQuestion(true);
    pushMessage({ role: 'user', text: question });
    setIsAgentResponding(true);
    setThinkingSteps([]);

    const controller = new AbortController();
    activeStreamRef.current = controller;
    const assistantId = msgId();
    let assistantBubbleStarted = false;
    let answerText = '';

    const upsertAssistantMessage = (updates: Partial<ChatMessage>) => {
      if (controller.signal.aborted) return;
      setMessages((prev) => {
        if (!assistantBubbleStarted) {
          assistantBubbleStarted = true;
          return [...prev, { id: assistantId, role: 'assistant', text: '', ...updates }];
        }
        return prev.map((m) => (m.id === assistantId ? { ...m, ...updates } : m));
      });
    };

    try {
      const response = await CustomsAnalyzerAPI.streamAgent(
        documentBatchId,
        question,
        userId,
        controller.signal,
      );
      if (!response.body) {
        throw new Error('The analyzer agent returned an empty response stream.');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done || controller.signal.aborted) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);

          if (event.type === 'text_delta') {
            answerText += event.text;
            upsertAssistantMessage({ text: answerText });
          } else if (event.type === 'tool_call') {
            const stepName = String(event.name || '');
            const stepMessage = event.message || `Calling ${stepName}…`;
            setThinkingSteps((prev) => [...prev, { name: stepName, message: stepMessage, done: false }]);
          } else if (event.type === 'tool_result') {
            const stepName = String(event.name || '');
            setThinkingSteps((prev) => {
              const lastPendingIndex = prev.map((s) => s.name).lastIndexOf(stepName);
              if (lastPendingIndex === -1) return prev;
              return prev.map((step, index) =>
                index === lastPendingIndex ? { ...step, done: true } : step,
              );
            });
          } else if (event.type === 'final') {
            const downloadLinks: ChatDownloadLink[] | undefined = Array.isArray(
              event.download_links,
            )
              ? event.download_links
              : undefined;
            answerText = event.answer || answerText || 'No response was returned.';
            upsertAssistantMessage({ text: answerText, downloadLinks: downloadLinks });
          } else if (event.type === 'error') {
            if (controller.signal.aborted) continue;
            pushMessage({
              role: 'error',
              text: event.message || 'Failed to contact the analyzer agent.',
            });
          }
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        const msg =
          err instanceof Error ? err.message : 'Failed to contact the analyzer agent.';
        pushMessage({ role: 'error', text: msg });
      }
    } finally {
      if (activeStreamRef.current === controller) {
        activeStreamRef.current = null;
        setIsAgentResponding(false);
      }
    }
  };

  const handleComposerSubmit = () => {
    if (isSubmitting || isAgentResponding) {
      return;
    }
    const note = input.trim();
    if (stagedFile) {
      const file = stagedFile;
      setStagedFile(null);
      setInput('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      runAnalysisFromComposer(file, note);
      return;
    }
    if (note) {
      setInput('');
      sendQuestion(note);
    }
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleComposerSubmit();
    }
  };

  const handleNewChat = () => {
    activeStreamRef.current?.abort();
    activeStreamRef.current = null;
    setMessages([]);
    setInput('');
    setStagedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setDocumentBatchId('');
    setAnalysisResponse(null);
    setIsSaved(false);
    setSavedTitle('');
    setHasAskedQuestion(false);
    setShowTryAsking(false);
    setIsAgentResponding(false);
    setIsSubmitting(false);
    setUploadProgress(null);
  };

  const canSendComposer = Boolean(input.trim()) || Boolean(stagedFile);
  const hasActiveBatch = Boolean(documentBatchId && analysisResponse);
  const assistantStatus = isSubmitting || isAgentResponding ? 'running' : 'idle';
  // Scrolls the two-column grid into view the first time the conversation
  // starts, same as EARChatUI / the other standard-workflow chat tools.
  const workflowGridRef = useScrollWorkflowIntoView(messages.length > 0);

  // The one result card that should default open -- everything before it in
  // the conversation has already been superseded by a newer run and stays
  // collapsed unless the user opens it themselves.
  const latestResultMessageId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].resultPayload) {
        return messages[index].id;
      }
    }
    return null;
  }, [messages]);

  // Shared composer -- rendered once centered in the empty state, and again
  // pinned to the bottom of the conversation once messages exist (same
  // empty/populated split EARChatUI uses for its WorkflowComposer), so
  // sending the first message visibly moves it from the middle of the card
  // down to the bottom. Uses plain DOM elements (not the TextField/IconButton
  // shim) so only the shared standard-workflow-composer-field CSS applies,
  // with no competing ce-field/ce-icon-button styles.
  const composer = (
    <div className='standard-workflow-composer'>
      {stagedFile && (
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          data-testid='selected-file-item'
          data-file-name={stagedFile.name}
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 2,
            border: '1px solid var(--es-soft-border, var(--border-color))',
            backgroundColor: 'var(--Background-200, var(--bg-colors))',
          }}
        >
          <DescriptionOutlinedIcon fontSize='small' sx={{ color: 'var(--action-color)' }} />
          <Typography sx={{ fontWeight: 700, flex: 1, wordBreak: 'break-word' }}>
            {stagedFile.name}
          </Typography>
          <Typography variant='caption' sx={{ color: 'var(--text-secondary)' }}>
            {formatFileSize(stagedFile.size)}
          </Typography>
          <IconButton
            aria-label={`Remove ${stagedFile.name}`}
            onClick={handleRemoveStagedFile}
            sx={{ width: 28, height: 28, fontSize: '18px' }}
          >
            ×
          </IconButton>
        </Stack>
      )}
      <div className='standard-workflow-composer-field'>
        <input
          ref={fileInputRef}
          hidden
          type='file'
          accept='application/pdf,.pdf'
          onChange={handleFileChange}
          data-testid='upload-file-input'
        />
        <button
          type='button'
          aria-label='Attach a PDF'
          data-testid='composer-attach-button'
          onClick={() => fileInputRef.current?.click()}
          disabled={isSubmitting}
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-secondary)',
          }}
        >
          <AddOutlinedIcon fontSize='small' />
        </button>
        <textarea
          ref={composerTextareaRef}
          rows={1}
          value={input}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setInput(event.target.value)}
          onKeyDown={handleComposerKeyDown}
          placeholder={
            hasActiveBatch
              ? 'Ask about line items, HTS codes, discrepancies… or attach a new PDF'
              : 'Attach a customs entry PDF and press Enter to run the analysis'
          }
          disabled={isSubmitting || isAgentResponding}
          aria-label='Message'
          data-testid='chat-input'
        />
        <button
          type='button'
          aria-label='Send message'
          data-testid='chat-send-button'
          onClick={handleComposerSubmit}
          disabled={!canSendComposer || isSubmitting || isAgentResponding}
        >
          <SendOutlinedIcon fontSize='small' />
        </button>
      </div>
      <div className='ce-chat__hint' style={{ textAlign: 'center' }}>
        Enter to send · Shift+Enter for newline · Attach one PDF to run an analysis
      </div>
    </div>
  );

  return (
    <section
      className={`standard-workflow-page${embedded ? ' standard-workflow-page--embedded' : ''}`}
    >
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      {!embedded && routeState?.returnTo && (
        <div style={{ marginBottom: '16px' }}>
          <ToolBreadcrumb
            items={[
              { label: 'Import Hub', to: '/import-hub/databases' },
              {
                label: routeState.returnTo.label,
                to: routeState.returnTo.path,
              },
              { label: 'Upload Documents' },
            ]}
          />
        </div>
      )}
      {!embedded && (
        <header className='standard-workflow-page-header'>
          <div>
            <h1>
              Document Upload and Analysis
              {/* Inline styles, not the shared .beta-tag class: that class is
                  force-hidden by `.SideNavBar .beta-tag` whenever the sidebar
                  collapses (`.SideNavBar` lives on #MainApp, the whole app
                  root), which would hide this page tag too. */}
              <span
                style={{
                  marginLeft: '8px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#1e40af',
                  backgroundColor: '#dbeafe',
                  borderRadius: '8px',
                  verticalAlign: 'middle',
                  letterSpacing: '0.02em',
                }}
              >
                Beta
              </span>
            </h1>
            <p>Attach a customs entry PDF in the chat below and get results back the same way.</p>
          </div>
          <div className='standard-workflow-page-actions'>
            <button
              type='button'
              className='standard-workflow-secondary-btn'
              data-testid='new-chat-button'
              onClick={handleNewChat}
            >
              New chat
            </button>
            <button
              type='button'
              className='standard-workflow-primary-btn'
              data-testid='save-analysis-button'
              onClick={() => setIsSaveModalOpen(true)}
              disabled={!documentBatchId}
            >
              {isSaved ? 'Saved ✓' : 'Save'}
            </button>
          </div>
        </header>
      )}

      <div className='standard-workflow-notice' role='note'>
        <span className='standard-workflow-notice-icon' aria-hidden='true'>
          i
        </span>
        <div className='standard-workflow-notice-content'>
          Assists with reviewing parsed customs entry data; not a substitute for a licensed
          customs broker. Always verify parsed data and discrepancies against your source
          documents before filing.
        </div>
      </div>

      <Modal open={isSaveModalOpen} onClose={() => setIsSaveModalOpen(false)}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType='customs-entry-analysis'
          handleSaveTicket={handleSaveAsTicket}
          newTicketName={savedTitle}
          setNewTicketName={setSavedTitle}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={savingTicket}
        />
      </Modal>

      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setPendingLocation(null);
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
        data-testid='leave-confirm-modal'
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Customs Entry Analyzer?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => {
              setIsLeaveModalOpen(false);
              setPendingLocation(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this page will discard your current analysis. Use Save
              to store it before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
                data-testid='leave-without-saving-button'
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className={
                  documentBatchId ? 'btn-new-automate' : 'btn-new-automate-disabled'
                }
                onClick={handleSaveFromLeaveModal}
                disabled={!documentBatchId}
                data-testid='leave-save-button'
              >
                <div>Save</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
                data-testid='leave-opt-out-button'
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Modal open={importHubModalOpen} onClose={handleCloseImportHubModal}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: importHubStep === 'select-database' ? 420 : 860,
            maxWidth: '92vw',
            maxHeight: '85vh',
            overflowY: 'auto',
            p: 2.5,
            ...helperCardSx,
            outline: 'none',
          }}
        >
          <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
            Send to Import Hub
          </Typography>

          {importHubStep === 'select-database' && useLockedPreset && (
            <>
              <Typography variant='body2' sx={{ color: 'var(--text-secondary)', mb: 1.5 }}>
                Sending to <strong>{presetDatabaseName || 'this database'}</strong> — the
                database this upload was started from.
              </Typography>
              {importHubError && (
                <Alert severity='error' sx={{ mb: 1.5 }} data-testid='import-hub-error-alert'>
                  {importHubError}
                </Alert>
              )}
              <Stack direction='row' spacing={1} justifyContent='flex-end'>
                <Button variant='outlined' onClick={handleCloseImportHubModal} disabled={isSendingToImportHub}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleImportHubNext}
                  disabled={isSendingToImportHub}
                  data-testid='import-hub-next'
                >
                  {isSendingToImportHub ? 'Preparing…' : 'Next: review products'}
                </Button>
              </Stack>
            </>
          )}

          {importHubStep === 'select-database' && !useLockedPreset && (
            <>
              <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  variant={importHubMode === 'existing' ? 'contained' : 'outlined'}
                  onClick={() => setImportHubMode('existing')}
                  disabled={!importHubDatabases.length}
                  data-testid='import-hub-mode-existing'
                >
                  Use existing
                </Button>
                <Button
                  variant={importHubMode === 'new' ? 'contained' : 'outlined'}
                  onClick={() => setImportHubMode('new')}
                  data-testid='import-hub-mode-new'
                >
                  Create new
                </Button>
              </Stack>

              {importHubMode === 'existing' ? (
                importHubDatabases.length ? (
                  <Stack spacing={1} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                    {importHubDatabases.map((db: any) => {
                      const checked = db.id === importHubSelectedId;
                      return (
                        <Box
                          key={db.id}
                          role='radio'
                          aria-checked={checked}
                          tabIndex={0}
                          onClick={() => setImportHubSelectedId(db.id)}
                          data-testid='import-hub-existing-option'
                          sx={{
                            ...modeCardSx,
                            ...(checked
                              ? { borderColor: 'var(--action-color)', boxShadow: '0 0 0 1px var(--action-color)' }
                              : {}),
                          }}
                        >
                          <Stack direction='row' spacing={1.25} alignItems='center'>
                            <Radio checked={checked} />
                            <Typography sx={{ fontWeight: 700 }}>
                              {db.name || db.database_name || db.id}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                    No existing Import Hub databases yet -- switch to &quot;Create new&quot;.
                  </Typography>
                )
              ) : (
                <TextField
                  fullWidth
                  size='small'
                  placeholder='New database name'
                  value={importHubNewName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setImportHubNewName(e.target.value)}
                  data-testid='import-hub-new-name-input'
                />
              )}

              {importHubError && (
                <Alert severity='error' sx={{ mt: 1.5 }} data-testid='import-hub-error-alert'>
                  {importHubError}
                </Alert>
              )}

              <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mt: 2 }}>
                <Button variant='outlined' onClick={handleCloseImportHubModal} disabled={isSendingToImportHub}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleImportHubNext}
                  disabled={isSendingToImportHub}
                  data-testid='import-hub-next'
                >
                  {isSendingToImportHub ? 'Preparing…' : 'Next: review products'}
                </Button>
              </Stack>
            </>
          )}

          {importHubStep === 'review' && (
            <>
              <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
                <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                  {importHubSelectedRowIds.size} of {importHubReviewRows.length} selected --{' '}
                  {
                    importHubReviewRows.filter(
                      (row) =>
                        importHubSelectedRowIds.has(row._client_row_id) &&
                        row._action !== 'skip',
                    ).length
                  }{' '}
                  will be sent. Duplicate records are automatically skipped.
                </Typography>
                <Button
                  variant='outlined'
                  onClick={() => runImportHubDuplicateCheck(importHubDatabaseId, importHubReviewRows)}
                  disabled={importHubIsCheckingDuplicates}
                  data-testid='import-hub-recheck'
                >
                  {importHubIsCheckingDuplicates ? 'Checking…' : 'Recheck for duplicates'}
                </Button>
              </Stack>

              <Box sx={{ overflowX: 'auto', border: '1px solid var(--Background-400, #37393f)', borderRadius: '6px' }}>
                <table className='ce-review-table' style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: 8, textAlign: 'left' }}>
                        <input
                          type='checkbox'
                          checked={
                            importHubReviewRows.length > 0 &&
                            importHubSelectedRowIds.size === importHubReviewRows.length
                          }
                          onChange={handleToggleAllImportHubRowsSelected}
                          aria-label='Select all rows'
                        />
                      </th>
                      <th style={{ padding: 8, textAlign: 'left' }}>Name</th>
                      <th style={{ padding: 8, textAlign: 'left' }}>HTS code</th>
                      <th style={{ padding: 8, textAlign: 'left' }}>Country</th>
                      <th style={{ padding: 8, textAlign: 'left' }}>Manufacturer</th>
                      <th style={{ padding: 8, textAlign: 'right' }}>Value</th>
                      <th style={{ padding: 8, textAlign: 'right' }}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importHubReviewRows.map((row) => {
                      const clientIssues = getProductRowIssues(row);
                      const warning = importHubRowWarnings[row._client_row_id];
                      const isSkipped = row._action === 'skip';
                      return (
                        <React.Fragment key={row._client_row_id}>
                          <tr
                            style={{
                              borderTop: '1px solid var(--Background-400, #37393f)',
                              opacity: isSkipped ? 0.5 : 1,
                            }}
                          >
                            <td style={{ padding: 8 }}>
                              <input
                                type='checkbox'
                                checked={importHubSelectedRowIds.has(row._client_row_id)}
                                onChange={() => handleToggleImportHubRowSelected(row._client_row_id)}
                                aria-label={`Select ${row.name}`}
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 160 }}>
                              <input
                                className='ce-field__control'
                                value={row.name || ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(row._client_row_id, 'name', e.target.value)
                                }
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 110 }}>
                              <input
                                className='ce-field__control'
                                value={row.hts_code || ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(row._client_row_id, 'hts_code', e.target.value)
                                }
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 90 }}>
                              <input
                                className='ce-field__control'
                                value={row.country_of_origin || ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(
                                    row._client_row_id,
                                    'country_of_origin',
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 110 }}>
                              <input
                                className='ce-field__control'
                                value={row.manufacturer || ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(row._client_row_id, 'manufacturer', e.target.value)
                                }
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 90 }}>
                              <input
                                className='ce-field__control'
                                style={{ textAlign: 'right' }}
                                value={row.product_value ?? ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(row._client_row_id, 'product_value', e.target.value)
                                }
                              />
                            </td>
                            <td style={{ padding: 8, minWidth: 70 }}>
                              <input
                                className='ce-field__control'
                                style={{ textAlign: 'right' }}
                                value={row.quantity ?? ''}
                                onChange={(e) =>
                                  handleUpdateImportHubRowField(row._client_row_id, 'quantity', e.target.value)
                                }
                              />
                            </td>
                          </tr>
                          {(clientIssues.length > 0 || warning) && (
                            <tr>
                              <td />
                              <td colSpan={6} style={{ padding: '0 8px 8px' }}>
                                {clientIssues.length > 0 && (
                                  <Typography variant='body2' sx={{ color: 'var(--low-color)' }}>
                                    {clientIssues.join(' ')}
                                  </Typography>
                                )}
                                {warning && (
                                  <Stack direction='row' spacing={1} alignItems='center'>
                                    <Typography variant='body2' sx={{ color: 'var(--mid-color)' }}>
                                      {warning}
                                    </Typography>
                                  </Stack>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </Box>

              {importHubError && (
                <Alert severity='error' sx={{ mt: 1.5 }} data-testid='import-hub-error-alert'>
                  {importHubError}
                </Alert>
              )}

              <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mt: 2 }}>
                <Button
                  variant='outlined'
                  onClick={() => setImportHubStep('select-database')}
                  disabled={isSendingToImportHub}
                >
                  Back
                </Button>
                <Button variant='outlined' onClick={handleCloseImportHubModal} disabled={isSendingToImportHub}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleConfirmSendToImportHub}
                  disabled={
                    isSendingToImportHub ||
                    importHubIsCheckingDuplicates ||
                    !importHubSelectedRowIds.size
                  }
                  data-testid='import-hub-confirm-send'
                >
                  {isSendingToImportHub
                    ? 'Sending…'
                    : `Send ${
                        importHubReviewRows.filter(
                          (row) =>
                            importHubSelectedRowIds.has(row._client_row_id) &&
                            row._action !== 'skip',
                        ).length
                      } product${
                        importHubReviewRows.filter(
                          (row) =>
                            importHubSelectedRowIds.has(row._client_row_id) &&
                            row._action !== 'skip',
                        ).length === 1
                          ? ''
                          : 's'
                      }`}
                </Button>
              </Stack>
            </>
          )}

          {importHubStep === 'results' && importHubResult && (
            <>
              <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
                <Typography variant='body2'>
                  <strong>{importHubResult.created.length}</strong> created
                </Typography>
                <Typography variant='body2'>
                  <strong>{importHubResult.skipped.length}</strong> skipped
                </Typography>
                <Typography variant='body2' sx={{ color: importHubResult.failed.length ? 'var(--low-color)' : undefined }}>
                  <strong>{importHubResult.failed.length}</strong> failed
                </Typography>
              </Stack>

              {(importHubResult.skipped.length > 0 || importHubResult.failed.length > 0) && (
                <Stack spacing={1} sx={{ maxHeight: 360, overflowY: 'auto', mb: 2 }}>
                  {importHubResult.failed.map((item, i) => {
                    const isHtsFailure = isHtsScheduleFailure(item.reason);
                    const fix = importHubHtsFix[item.client_row_id];
                    return (
                      <Box key={`failed-${i}`}>
                        <Typography variant='body2' sx={{ color: 'var(--low-color)' }}>
                          Failed -- {item.name || 'Unnamed product'}: {item.reason}
                        </Typography>
                        {isHtsFailure && (
                          <Box sx={{ pl: 2, pt: 0.5 }}>
                            <Typography
                              variant='body2'
                              sx={{ color: 'var(--text-secondary)', mb: 0.5 }}
                              data-testid='import-hub-hts-failure-explainer'
                            >
                              This HTS code was valid when the entry was filed, but the tariff
                              schedule has since changed and it&apos;s no longer on the current
                              list -- it isn&apos;t a data error on this import.
                            </Typography>
                            {!fix && (
                              <Button
                                variant='text'
                                size='small'
                                onClick={() => handleFindHtsReplacements(item.client_row_id)}
                                data-testid='import-hub-find-hts-replacement'
                              >
                                Find a current replacement
                              </Button>
                            )}
                            {fix?.status === 'loading' && (
                              <Stack direction='row' spacing={1} alignItems='center'>
                                <CircularProgress size={14} />
                                <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                                  Looking up codes currently in the USHTS schedule...
                                </Typography>
                              </Stack>
                            )}
                            {fix?.status === 'error' && (
                              <Typography variant='body2' sx={{ color: 'var(--low-color)' }}>
                                {fix.error}
                              </Typography>
                            )}
                            {fix?.status === 'loaded' && (
                              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                {fix.options.length > 0 ? (
                                  <Stack spacing={0.5} sx={{ maxHeight: 160, overflowY: 'auto' }}>
                                    {fix.options.map((option) => {
                                      const checked = fix.selectedCode === option.code;
                                      return (
                                        <Box
                                          key={option.code}
                                          role='radio'
                                          aria-checked={checked}
                                          tabIndex={0}
                                          onClick={() =>
                                            setImportHubHtsFix((prev) => ({
                                              ...prev,
                                              [item.client_row_id]: {
                                                ...prev[item.client_row_id],
                                                selectedCode: option.code,
                                                manualCode: '',
                                              },
                                            }))
                                          }
                                          data-testid='import-hub-hts-replacement-option'
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 0.75,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            border: '1px solid var(--es-soft-border)',
                                            ...(checked
                                              ? {
                                                  borderColor: 'var(--action-color)',
                                                  boxShadow: '0 0 0 1px var(--action-color)',
                                                }
                                              : {}),
                                          }}
                                        >
                                          <Radio checked={checked} />
                                          <Typography variant='body2'>{option.label}</Typography>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                ) : (
                                  <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                                    No currently-effective codes found in this HTS family. Enter
                                    one manually below.
                                  </Typography>
                                )}
                                <TextField
                                  size='small'
                                  placeholder='Or enter an HTS code manually'
                                  value={fix.manualCode}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setImportHubHtsFix((prev) => ({
                                      ...prev,
                                      [item.client_row_id]: {
                                        ...prev[item.client_row_id],
                                        manualCode: e.target.value,
                                        selectedCode: '',
                                      },
                                    }))
                                  }
                                  data-testid='import-hub-hts-manual-input'
                                />
                                <Stack direction='row' spacing={1} alignItems='center'>
                                  <Button
                                    variant='outlined'
                                    size='small'
                                    disabled={
                                      fix.retrying || !(fix.selectedCode || fix.manualCode.trim())
                                    }
                                    onClick={() =>
                                      handleRetryRowWithHts(
                                        item.client_row_id,
                                        fix.selectedCode || fix.manualCode,
                                      )
                                    }
                                    data-testid='import-hub-retry-with-hts'
                                  >
                                    {fix.retrying ? 'Retrying...' : 'Retry with this code'}
                                  </Button>
                                  {fix.error && (
                                    <Typography variant='body2' sx={{ color: 'var(--low-color)' }}>
                                      {fix.error}
                                    </Typography>
                                  )}
                                </Stack>
                              </Stack>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {importHubResult.skipped.map((item, i) => (
                    <Typography key={`skipped-${i}`} variant='body2' sx={{ color: 'var(--mid-color)' }}>
                      Skipped -- {item.name || 'Unnamed product'}: {item.reason}
                    </Typography>
                  ))}
                </Stack>
              )}

              <Stack direction='row' spacing={1} justifyContent='flex-end'>
                <Button variant='contained' onClick={handleCloseImportHubModal} data-testid='import-hub-done'>
                  Done
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Modal>

      <div
        ref={workflowGridRef}
        className='standard-workflow-grid chat-workflow-row'
        // The shared grid only sets grid-template-columns, so its single row
        // defaults to an auto/content-sized track -- once the chat card's
        // content (messages + the absolutely-positioned composer) grows
        // taller than the intended clamp() height, the row (and the card's
        // "height:100%") grows right along with it instead of clipping,
        // letting the composer spill past the card's bottom edge into the
        // page below it. Pinning the row to the container's own height
        // forces the card back to a fixed height so its own overflow:hidden
        // + internal scroll actually take over.
        style={{ gridTemplateRows: 'minmax(0, 1fr)' }}
      >
        <div
          className='standard-workflow-assistant-card'
          data-testid='chat-panel'
          style={
            isDragActive ? { boxShadow: '0 0 0 2px rgba(3, 169, 244, 0.35)' } : undefined
          }
          onDragEnter={(event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragActive(true);
          }}
          onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleChatDrop}
        >
          <div className='standard-workflow-panel-header'>
            <div className='standard-workflow-panel-title'>
              <span className='standard-workflow-bot-icon' aria-hidden='true'>
                <ChatBubbleOutlineOutlinedIcon fontSize='small' />
              </span>
              <h2>Customs Entry Assistant</h2>
            </div>
            <div
              className={`standard-workflow-status ${assistantStatus}`}
              role='status'
              aria-live='polite'
            >
              <span aria-hidden='true' />
              {assistantStatus === 'running' ? 'Responding' : 'Idle'}
            </div>
          </div>

          {messages.length === 0 ? (
            <div
              className='standard-workflow-empty-chat'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {composer}
            </div>
          ) : (
          <div className='chat-container'>
            <div
              className='ce-chat__body'
              style={{ flex: 1, minHeight: 0, maxHeight: 'none', paddingBottom: 160 }}
            >
            {messages.map((msg) => {
              if (msg.resultPayload) {
                const payload = msg.resultPayload;
                return (
                  <div key={msg.id} className='ce-chat__message-row'>
                    <Box
                      className='ce-chat__message'
                      sx={{ maxWidth: '100%', width: '100%', p: 0, border: 'none', background: 'transparent' }}
                      data-testid='chat-message-assistant'
                    >
                      <AnalysisResultCard
                        payload={payload}
                        isLoadingArtifacts={isLoadingArtifacts}
                        isLatest={msg.id === latestResultMessageId}
                        onDownloadArtifact={(artifact, index) =>
                          handleArtifactDownload(payload.documentBatchId, artifact, index)
                        }
                        onDownloadAllArtifacts={(artifacts) =>
                          artifacts.forEach((artifact, index) =>
                            handleArtifactDownload(payload.documentBatchId, artifact, index),
                          )
                        }
                        onRefreshArtifacts={() =>
                          handleRefreshArtifacts(msg.id, payload.documentBatchId, payload.artifactList)
                        }
                        onRunEs003Diff={(file) =>
                          handleRunEs003Diff(msg.id, payload.documentBatchId, file)
                        }
                        onSendToTool={handleSendToToolOption}
                        onRevealTryAsking={handleRevealTryAsking}
                      />
                    </Box>
                  </div>
                );
              }

              if (msg.role === 'status') {
                return (
                  <div
                    key={msg.id}
                    className='ce-chat__message-row'
                    data-testid='chat-message-status'
                  >
                    <div className='standard-workflow-thought-block'>
                      <div
                        className='standard-workflow-thought-summary standard-workflow-thought-summary-live'
                        aria-hidden='true'
                      >
                        {formatThoughtDuration(submittingElapsedSeconds)}
                        <span className='standard-workflow-thought-chevron' aria-hidden='true'>
                          &gt;
                        </span>
                      </div>
                      <div className='standard-workflow-thinking-card'>
                        <div className='standard-workflow-thinking-title'>
                          <span aria-hidden='true' />
                          Thinking
                        </div>
                        {(msg.steps && msg.steps.length ? msg.steps : [msg.text]).map((step, index) => (
                          <div
                            className='standard-workflow-thinking-step'
                            key={`${msg.id}-step-${index}`}
                            data-testid='thinking-step'
                          >
                            {step}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              const isUser = msg.role === 'user';
              const isError = msg.role === 'error';
              return (
                <div
                  key={msg.id}
                  className={cx('ce-chat__message-row', isUser && 'ce-chat__message-row--user')}
                >
                  <div
                    className={cx(
                      'ce-chat__message',
                      isUser && 'ce-chat__message--user',
                      isError && 'ce-chat__message--error',
                    )}
                    data-testid={`chat-message-${msg.role}`}
                    role={isError ? 'alert' : undefined}
                  >
                    {isUser ? (
                      <span className='ce-chat__user-text'>{msg.text}</span>
                    ) : (
                      <div className='ce-chat__markdown'>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                      </div>
                    )}
                    {!isUser && !isError && msg.downloadLinks && msg.downloadLinks.length > 0 && (
                      <Stack
                        direction='row'
                        spacing={1}
                        flexWrap='wrap'
                        useFlexGap
                        sx={{ mt: 1 }}
                        data-testid='chat-download-links'
                      >
                        {msg.downloadLinks.map((link) => (
                          <Button
                            key={link.path}
                            variant='outlined'
                            size='small'
                            startIcon={<DownloadOutlinedIcon fontSize='small' />}
                            aria-label={`Download ${link.label}`}
                            data-testid='chat-download-link'
                            disabled={downloadingPath === link.path}
                            onClick={() => handleDownloadLink(link)}
                          >
                            {downloadingPath === link.path ? 'Downloading…' : link.label}
                          </Button>
                        ))}
                      </Stack>
                    )}
                  </div>
                </div>
              );
            })}

            {hasActiveBatch && !hasAskedQuestion && !isSubmitting && !isAgentResponding && showTryAsking && (
              <div className='ce-chat__intro'>
                <div className='ce-chat__starter-label'>Try asking</div>
                <div className='ce-chat__starter-list'>
                  {STARTER_PROMPTS.map(({ label, prompt }) => (
                    <button
                      key={label}
                      type='button'
                      className='ce-chat__starter'
                      data-testid='chat-starter-prompt'
                      onClick={() => sendQuestion(prompt)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isAgentResponding && (
              <div className='ce-chat__message-row' data-testid='chat-loading'>
                <div className='standard-workflow-thought-block'>
                  <div
                    className='standard-workflow-thought-summary standard-workflow-thought-summary-live'
                    aria-hidden='true'
                  >
                    {formatThoughtDuration(thinkingElapsedSeconds)}
                    <span className='standard-workflow-thought-chevron' aria-hidden='true'>
                      &gt;
                    </span>
                  </div>
                  <div className='standard-workflow-thinking-card'>
                    <div className='standard-workflow-thinking-title'>
                      <span aria-hidden='true' />
                      Thinking
                    </div>
                    {thinkingSteps.map((step, index) => (
                      <div
                        className='standard-workflow-thinking-step'
                        key={`${step.name}-${index}`}
                        data-testid='thinking-step'
                      >
                        {step.done ? '✓ ' : ''}
                        {step.message}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
          {composer}
          </div>
          )}
        </div>

        <div className='standard-workflow-progress-card bill-container-fit-screen'>
          <div className='standard-workflow-progress-header'>
            <div className='standard-workflow-progress-heading'>
              <h2>Analysis options</h2>
              <span className='standard-workflow-badge'>Beta</span>
            </div>
            <p>Choose how the next attached PDF is processed.</p>
          </div>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', width: '100%', p: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack spacing={1.25} role='radiogroup' aria-label='Analysis mode'>
                {ANALYSIS_MODES.map((mode) => {
                  const checked = mode.value === analysisMode;
                  return (
                    <Box
                      key={mode.value}
                      role='radio'
                      aria-checked={checked}
                      aria-label={mode.label}
                      tabIndex={0}
                      data-testid={`mode-option-${mode.value}`}
                      onClick={() => setAnalysisMode(mode.value)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setAnalysisMode(mode.value);
                        }
                      }}
                      sx={{
                        ...modeCardSx,
                        ...(checked
                          ? {
                              borderColor: 'var(--action-color)',
                              boxShadow: '0 0 0 1px var(--action-color)',
                              backgroundColor: 'rgba(3, 169, 244, 0.08)',
                            }
                          : {
                              borderColor: 'var(--Colors-Border-border-primary, #333741)',
                              boxShadow: 'none',
                            }),
                      }}
                    >
                      <Stack direction='row' spacing={1.25} alignItems='center'>
                        <Radio checked={checked} />
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: 'var(--text-primary-color)' }}>
                            {mode.label}
                          </Typography>
                          <Typography variant='body2' sx={{ color: 'var(--text-secondary)' }}>
                            {mode.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              <Divider sx={{ borderColor: 'var(--border-color)' }} />

              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                <Chip label={documentBatchId ? `Batch ${documentBatchId}` : 'No batch yet'} />
                <Chip
                  label={stagedFile ? '1 PDF attached' : 'No PDF attached'}
                  color={stagedFile ? 'primary' : 'default'}
                />
                <Chip label={`Mode: ${selectedModeLabel}`} />
              </Stack>

              {uploadProgress && isSubmitting && (
                <Typography variant='caption' sx={{ color: 'var(--text-secondary)' }}>
                  Uploading… {uploadProgress.percent}%
                </Typography>
              )}

              {documentBatchId && (
                <Button
                  variant='contained'
                  disabled={isSubmitting}
                  onClick={runModeOnCurrentBatch}
                  data-testid='run-mode-on-batch-button'
                >
                  Run {selectedModeLabel} on this batch
                </Button>
              )}

              {isSubmitting && (
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  data-testid='workflow-progress'
                  sx={{ color: 'var(--text-secondary)' }}
                >
                  <CircularProgress size={14} />
                  <Typography variant='body2'>Running {selectedModeLabel}…</Typography>
                </Stack>
              )}
              {!isSubmitting && hasActiveBatch && (
                <Stack direction='row' spacing={1} alignItems='center' sx={{ color: '#22c55e' }}>
                  <CheckCircleOutlineOutlinedIcon fontSize='small' />
                  <Typography variant='body2'>Latest batch analyzed.</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </div>
      </div>
    </section>
  );
};

export default CustomsEntryAnalyzer;
